// DynamicCertificateBuilder.jsx
// Full dynamic certificate template builder — left controls + live A4 preview.
// Includes: field manager (add/remove/hide/show/edit/drag), HTML editor,
// border design, background, signatures, and template library.

import React, { useState, useRef, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import idCardGreenBg from './assets/idcard-green-bg-body-olive.png';
import idCardBackWave from './assets/idcard-back-wave3-olive.png';
import * as Cookies from "es-cookie";   
import {
  VARIABLES,
  SAMPLE_DATA,
  replaceVariables,
  saveTemplateToStorage,
  loadTemplatesFromStorage,
  extractVariablesFromHtml,
  buildFieldsFromKeys,
} from './templates/templateEngine';

import {
  CERT_TYPES,
  BORDER_PRESETS,
  TYPE_PRESETS,
  TEMPLATE_LIBRARY,
} from './templates/certificatePresets';

import './_dynamicBuilder.scss';

// ── Small reusable pieces ────────────────────────────────────────────────

const Section = ({ title, open, onToggle, children }) => (
  <div className="dcb-section">
    <button className="dcb-section-hdr" onClick={onToggle}>
      <span>{title}</span>
      <span className={`dcb-arrow${open ? ' dcb-arrow--open' : ''}`}>▾</span>
    </button>
    {open && <div className="dcb-section-body">{children}</div>}
  </div>
);

const FRow = ({ label, children }) => (
  <div className="dcb-frow">
    <label className="dcb-flabel">{label}</label>
    <div className="dcb-fcontrol">{children}</div>
  </div>
);

// ── Default fields (TC has the most fields by default) ───────────────────

const DEFAULT_TC_FIELDS = [
  'student_name','father_name','mother_name','admission_no','dob',
  'religion','nationality','class_name','section_name','academic_year',
  'conduct','tc_no','serial_no','date',
];

const DEFAULT_STUDY_FIELDS = [
  'student_name','father_name','admission_no','class_name','section_name',
  'academic_year','dob','religion','nationality','conduct','date',
];

const DEFAULT_BONAFIDE_FIELDS = [
  'student_name','father_name','admission_no','class_name','academic_year','dob','date',
];

const DEFAULT_RECORD_FIELDS = [
  'admission_no','student_name','dob','father_name','mother_name',
  'nationality','religion','class_name','section_name','academic_year','conduct','date','school_code',
];

const DEFAULT_IDCARD_FIELDS = [
  'student_name','father_name','class_name','section_name',
  'dob','student_address','phone','blood_group','student_photo',
];

const TYPE_DEFAULT_FIELDS = {
  tc:          DEFAULT_TC_FIELDS,
  study:       DEFAULT_STUDY_FIELDS,
  bonafide:    DEFAULT_BONAFIDE_FIELDS,
  recordsheet: DEFAULT_RECORD_FIELDS,
  idcard:      DEFAULT_IDCARD_FIELDS,
};

// Fields that render in the certificate's TOP header (not in the body table) —
// excluded from the Dynamic Fields manager and shown at the top of the preview.
const HEADER_FIELD_KEYS = {
  tc:          ['admission_no', 'tc_no', 'serial_no', 'date'],
  conduct:     ['admission_no', 'tc_no', 'serial_no', 'date'],
  recordsheet: ['date', 'school_code'],
};

const getHeaderFieldKeys = (certType) => HEADER_FIELD_KEYS[certType] || [];

// ── Font options ─────────────────────────────────────────────────────────

const FONT_OPTIONS = [
  { value: "'Times New Roman', Times, serif",       label: 'Times New Roman'  },
  { value: "Georgia, 'Times New Roman', serif",     label: 'Georgia'          },
  { value: "'Palatino Linotype', Palatino, serif",  label: 'Palatino'         },
  { value: "Arial, Helvetica, sans-serif",          label: 'Arial'            },
  { value: "'Segoe UI', Tahoma, Geneva, sans-serif",label: 'Segoe UI'         },
  { value: "Verdana, Geneva, sans-serif",           label: 'Verdana'          },
  { value: "'Trebuchet MS', Helvetica, sans-serif", label: 'Trebuchet MS'     },
  { value: "'Century Gothic', CenturyGothic, AppleGothic, sans-serif", label: 'Century Gothic' },
  { value: "Calibri, Candara, Segoe, sans-serif",   label: 'Calibri'          },
  { value: "'Courier New', Courier, monospace",     label: 'Courier New'      },
  { value: "'Book Antiqua', Palatino, serif",       label: 'Book Antiqua'     },
  { value: "Garamond, 'Times New Roman', serif",    label: 'Garamond'         },
];

// ── Default config ───────────────────────────────────────────────────────

const DEFAULT_CFG = {
  templateName: '',
  branchId: '',
  certType: 'tc',
  fontFamily: "'Times New Roman', Times, serif",

  // Header — logo, name and address default to the logged-in institution's
  // values stored in cookies at login (url = logo, institutename, address).
  logoUrl: Cookies.get('url') || null,
  schoolName: Cookies.get('institutename') || '',
  address: Cookies.get('address') || '',
  phone: '',
  recognitionText: 'Recognised by the Govt. of T.G.',
  medium_label: '',
  edu_type: '',
  certTitle: 'TRANSFER CERTIFICATE',
  alignment: 'center',
  schoolNameSize: 22,
  schoolNameColor: '#000000',
  certTitleSize: 16,
  certTitleColor: '#1a237e',

  // Border
  borderPreset: 'classic',
  borderStyle: 'solid',
  borderThickness: 3,
  borderColor: '#1a237e',
  borderRadius: 0,
  innerBorder: true,
  innerBorderColor: '#1a237e',
  innerBorderThickness: 1.5,
  innerBorderInset: 8,
  borderImageUrl: null,

  // Background
  bgType: 'white',
  bgColor: '#ffffff',
  bgGradientStart: '#ffffff',
  bgGradientEnd: '#e8e4ff',
  bgGradientDir: 'to bottom',
  bgImageUrl: '',
  watermarkUrl: '',
  watermarkOpacity: 0.08,

  // Body
  bodyContent: TYPE_PRESETS.tc.bodyContent,
  bodyFontSize: 14,
  bodyLineHeight: 2.0,

  // Dynamic fields
  fields: buildFieldsFromKeys(DEFAULT_TC_FIELDS),

  // Signatures
  signatures: [
    { label: 'Class Teacher', align: 'left' },
    { label: 'Head of the Institution', align: 'right' },
  ],

  // Footer
  footerEnabled:    true,
  footerText:       '',
  footerFontSize:   11,
  footerColor:      '#555555',
  footerAlignment:  'center',
  footerDivider:    true,
  footerBg:         'transparent',

  // Bonafide structured line editor
  bonafideLines:          null,
  bonafideConductEnabled: true,
  bonafideConductText:    'His / Her Character & Conduct found to be good.',
  bonafideConductSize:    15,
  bonafideFooterLines:    [],

  // ID Card preview photo (builder-side sample; student data overrides at generate time)
  idCardPhotoUrl:         null,

  // Record Sheet — UDISE number shown in the header (editable per template)
  schoolCode:             '36221191912',
};

// ── ID Card helpers ───────────────────────────────────────────────────────

const ID_SPECIAL_FIELDS = new Set(['student_name','class_name','section_name','student_photo']);

function idCardRow(label, value, lc, vc) {
  return `<tr>
    <td style="padding:2.5px 5px 2.5px 0;color:${lc};font-size:10.5px;min-width:70px;vertical-align:top;white-space:nowrap;">${label}</td>
    <td style="padding:2.5px 3px;color:${lc};font-size:10px;vertical-align:top;">:</td>
    <td style="padding:2.5px 0;font-weight:600;font-size:10.5px;color:${vc};word-break:break-word;">${value || '—'}</td>
  </tr>`;
}

function getIDCardDetailFields(cfg) {
  if (cfg.fields && cfg.fields.length > 0) {
    return cfg.fields.filter(f => f.visible && !ID_SPECIAL_FIELDS.has(f.key));
  }
  return [
    { key: 'father_name',    label: 'Father Name'  },
    { key: 'dob',            label: 'Date of Birth' },
    { key: 'student_address',label: 'Address'       },
    { key: 'phone',          label: 'Phone'         },
    { key: 'blood_group',    label: 'Blood Group'   },
  ];
}

// Style 1 — Purple Wave (classic)
function buildIDCardWave(cfg, d, ff, dFields, sigImg, photoStyle = 'object-fit:cover;object-position:center 25%;') {
  const ac = cfg.certTitleColor || '#6a0dad';
  const al = '#9c27b0';
  const logo = cfg.logoUrl
    ? `<img src="${cfg.logoUrl}" alt="logo" style="width:50px;height:50px;object-fit:contain;border-radius:50%;position:absolute;top:12px;left:12px;background:#fff;padding:3px;box-shadow:0 2px 6px rgba(0,0,0,0.18);"/>`
    : '';
  const pSrc = d.student_photo || '';
  const photoFallback = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='35' r='25' fill='%23c4b5fd'/%3E%3Cellipse cx='50' cy='92' rx='38' ry='28' fill='%23c4b5fd'/%3E%3C/svg%3E`;
  const photo = pSrc
    ? `<img src="${pSrc}" style="width:100%;height:100%;${photoStyle}border-radius:50%;" onerror="this.onerror=null;this.src='${photoFallback}';"/>`
    : `<img src="${photoFallback}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;"/>`;
  const rows = dFields.map(f => idCardRow(f.label, d[f.key], '#666', f.key === 'blood_group' ? '#c62828' : '#222')).join('');
  return `<div style="width:100%;display:flex;justify-content:center;padding:28px 0;background:#f0f0f0;min-height:480px;box-sizing:border-box;">
  <div style="width:280px;border-radius:18px;overflow:hidden;box-shadow:0 6px 24px rgba(0,0,0,0.2);font-family:${ff};background:#fff;position:relative;">
    <div style="background:linear-gradient(155deg,${ac},${al});padding:16px 60px 66px;position:relative;text-align:center;min-height:120px;">
      ${logo}
      <div style="color:#fff;font-weight:900;font-size:13px;letter-spacing:0.4px;text-transform:uppercase;line-height:1.3;word-break:break-word;">${cfg.schoolName || 'SCHOOL NAME'}</div>
      ${cfg.address ? `<div style="color:rgba(255,255,255,0.8);font-size:10px;margin-top:2px;">${cfg.address}</div>` : ''}
      <div style="position:absolute;bottom:-1px;left:0;right:0;"><svg viewBox="0 0 280 52" preserveAspectRatio="none" style="display:block;width:100%;height:52px;"><path d="M0,36 Q70,10 140,30 Q210,50 280,20 L280,52 L0,52 Z" fill="#fff"/></svg></div>
      <div style="position:absolute;bottom:-46px;left:50%;transform:translateX(-50%);width:92px;height:92px;border-radius:50%;border:4px solid #fff;box-shadow:0 3px 10px rgba(106,13,173,0.3);overflow:hidden;z-index:2;background:#ede7f6;">${photo}</div>
    </div>
    <div style="padding:54px 18px 10px;text-align:center;">
      <div style="font-size:17px;font-weight:900;color:#1a1a1a;text-transform:uppercase;letter-spacing:0.4px;">${d.student_name || 'STUDENT NAME'}</div>
      <div style="display:inline-block;background:#ede7f6;color:${ac};font-size:11px;font-weight:700;border-radius:20px;padding:3px 14px;margin-top:5px;">Std. ${d.class_name || 'CLASS'} &nbsp;|&nbsp; ${d.section_name || 'SECTION'}</div>
      <table style="margin-top:12px;width:100%;border-collapse:collapse;text-align:left;"><tbody>${rows || `<tr><td colspan="3" style="text-align:center;color:#bbb;font-size:11px;padding:6px;">No fields</td></tr>`}</tbody></table>
    </div>
    <div style="padding:4px 18px 46px;display:flex;justify-content:flex-end;">
      <div style="text-align:center;min-width:92px;">
        ${sigImg ? `<img src="${sigImg}" style="max-width:88px;max-height:30px;object-fit:contain;display:block;margin:0 auto 4px;" />` : `<div style="height:32px;border-bottom:1.5px solid #bbb;margin-bottom:4px;"></div>`}
        <div style="font-size:10px;font-weight:600;color:#888;letter-spacing:0.3px;">Principal</div>
      </div>
    </div>
    <div style="position:absolute;bottom:0;left:0;right:0;"><svg viewBox="0 0 280 36" preserveAspectRatio="none" style="display:block;width:100%;height:36px;"><path d="M0,26 Q70,7 140,22 Q210,37 280,13 L280,36 L0,36 Z" fill="${ac}" opacity="0.45"/><path d="M0,31 Q70,12 140,28 Q210,44 280,18 L280,36 L0,36 Z" fill="${ac}" opacity="0.9"/></svg></div>
  </div></div>`;
}

// Style 2 — Teal Split (left panel photo, right panel details)
function buildIDCardSidebar(cfg, d, ff, dFields, sigImg, photoStyle = 'object-fit:cover;object-position:center 25%;') {
  const ac = cfg.certTitleColor || '#00897b';
  const pSrc = d.student_photo || '';
  const photoFallback2 = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='35' r='25' fill='%23fff'/%3E%3Cellipse cx='50' cy='92' rx='38' ry='28' fill='%23fff'/%3E%3C/svg%3E`;
  const photo = pSrc
    ? `<img src="${pSrc}" style="width:80px;height:80px;${photoStyle}border-radius:50%;border:3px solid #fff;display:block;" onerror="this.onerror=null;this.src='${photoFallback2}';"/>`
    : `<img src="${photoFallback2}" style="width:80px;height:80px;object-fit:cover;border-radius:50%;border:3px solid #fff;display:block;"/>`;
  const logo = cfg.logoUrl
    ? `<img src="${cfg.logoUrl}" alt="logo" style="width:28px;height:28px;object-fit:contain;border-radius:50%;background:#fff;padding:2px;margin-bottom:6px;display:block;"/>`
    : '';
  const rows = dFields.map(f => idCardRow(f.label, d[f.key], '#555', f.key === 'blood_group' ? '#c62828' : '#222')).join('');
  return `<div style="width:100%;display:flex;justify-content:center;padding:28px 0;background:#e0f2f1;min-height:480px;box-sizing:border-box;">
  <div style="width:310px;border-radius:14px;overflow:hidden;box-shadow:0 6px 22px rgba(0,0,0,0.16);font-family:${ff};display:flex;flex-direction:row;">
    <div style="width:110px;background:linear-gradient(180deg,${ac},#00695c);display:flex;flex-direction:column;align-items:center;padding:20px 8px 14px;flex-shrink:0;">
      ${logo}
      <div style="margin-top:${logo ? '2px' : '14px'};">${photo}</div>
      <div style="color:#fff;font-size:10px;font-weight:700;margin-top:10px;text-align:center;line-height:1.4;">${d.class_name || 'CLASS'}</div>
      <div style="background:rgba(255,255,255,0.2);color:#fff;font-size:9px;font-weight:600;border-radius:10px;padding:2px 8px;margin-top:3px;">${d.section_name || 'SECTION'}</div>
      <div style="flex:1;"></div>
      <div style="color:rgba(255,255,255,0.65);font-size:8px;text-align:center;margin-top:6px;letter-spacing:1px;">ID CARD</div>
    </div>
    <div style="flex:1;background:#fff;padding:14px 13px 12px;display:flex;flex-direction:column;min-height:0;">
      <div style="color:${ac};font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid ${ac};padding-bottom:5px;margin-bottom:6px;">${cfg.schoolName || 'SCHOOL NAME'}</div>
      ${cfg.address ? `<div style="color:#999;font-size:9px;margin-bottom:6px;">${cfg.address}</div>` : ''}
      <div style="font-size:14px;font-weight:900;color:#1a1a1a;text-transform:uppercase;line-height:1.2;margin-bottom:3px;word-break:break-word;">${d.student_name || 'STUDENT NAME'}</div>
      <div style="height:1px;background:#e8e8e8;margin:7px 0;"></div>
      <table style="width:100%;border-collapse:collapse;"><tbody>${rows || `<tr><td colspan="3" style="color:#bbb;font-size:11px;padding:4px;">No fields</td></tr>`}</tbody></table>
      <div style="margin-top:auto;padding-top:10px;display:flex;justify-content:flex-end;">
        <div style="text-align:center;min-width:82px;">
          ${sigImg ? `<img src="${sigImg}" style="max-width:78px;max-height:26px;object-fit:contain;display:block;margin:0 auto 3px;" />` : `<div style="height:28px;border-bottom:1.5px solid #ccc;margin-bottom:3px;"></div>`}
          <div style="font-size:9px;color:#aaa;letter-spacing:0.3px;">Principal</div>
        </div>
      </div>
    </div>
  </div></div>`;
}

// Style 3 — Red Formal (academic)
function buildIDCardFormal(cfg, d, ff, dFields, sigImg, photoStyle = 'object-fit:cover;object-position:center 25%;') {
  const ac = cfg.certTitleColor || '#b71c1c';
  const logo = cfg.logoUrl
    ? `<img src="${cfg.logoUrl}" alt="logo" style="width:34px;height:34px;object-fit:contain;border-radius:4px;background:#fff;padding:2px;flex-shrink:0;"/>`
    : '';
  const pSrc = d.student_photo || '';
  const photoFallback3 = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Ccircle cx='50' cy='38' r='28' fill='%23fca5a5'/%3E%3Cellipse cx='50' cy='100' rx='40' ry='30' fill='%23fca5a5'/%3E%3C/svg%3E`;
  const photo = pSrc
    ? `<img src="${pSrc}" style="width:72px;height:86px;${photoStyle}border-radius:4px;border:2px solid ${ac};display:block;" onerror="this.onerror=null;this.src='${photoFallback3}';"/>`
    : `<img src="${photoFallback3}" style="width:72px;height:86px;object-fit:cover;border-radius:4px;border:2px solid ${ac};display:block;"/>`;
  const rows = dFields.map(f => idCardRow(f.label, d[f.key], '#555', f.key === 'blood_group' ? '#b71c1c' : '#222')).join('');
  return `<div style="width:100%;display:flex;justify-content:center;padding:28px 0;background:#fbe9e7;min-height:480px;box-sizing:border-box;">
  <div style="width:284px;border-radius:10px;overflow:hidden;box-shadow:0 4px 18px rgba(183,28,28,0.18);font-family:${ff};background:#fff;">
    <div style="background:${ac};padding:11px 13px;display:flex;align-items:center;gap:10px;">
      ${logo}
      <div>
        <div style="color:#fff;font-weight:900;font-size:12.5px;text-transform:uppercase;letter-spacing:0.4px;">${cfg.schoolName || 'SCHOOL NAME'}</div>
        ${cfg.address ? `<div style="color:rgba(255,255,255,0.78);font-size:9px;margin-top:1px;">${cfg.address}</div>` : ''}
        <div style="background:rgba(255,255,255,0.18);color:#fff;font-size:8.5px;font-weight:700;border-radius:3px;padding:2px 7px;margin-top:3px;display:inline-block;letter-spacing:0.8px;">STUDENT ID CARD</div>
      </div>
    </div>
    <div style="display:flex;gap:11px;padding:13px;">
      <div style="flex-shrink:0;">${photo}</div>
      <div style="flex:1;min-width:0;">
        <div style="font-size:13.5px;font-weight:900;color:#1a1a1a;text-transform:uppercase;line-height:1.2;word-break:break-word;">${d.student_name || 'STUDENT NAME'}</div>
        <div style="background:${ac};color:#fff;font-size:9px;font-weight:700;border-radius:3px;padding:2px 7px;margin-top:3px;display:inline-block;">Std. ${d.class_name || 'CLASS'} — ${d.section_name || 'SEC'}</div>
        <div style="height:1px;background:#eee;margin:7px 0;"></div>
        <table style="width:100%;border-collapse:collapse;"><tbody>${rows || `<tr><td colspan="3" style="color:#bbb;font-size:11px;padding:4px;">No fields</td></tr>`}</tbody></table>
      </div>
    </div>
    <div style="background:${ac};height:3px;"></div>
    <div style="padding:6px 13px 10px;display:flex;justify-content:flex-end;">
      <div style="text-align:center;min-width:88px;">
        ${sigImg ? `<img src="${sigImg}" style="max-width:84px;max-height:26px;object-fit:contain;display:block;margin:0 auto 3px;" />` : `<div style="height:28px;border-bottom:1.5px solid #ccc;margin-bottom:3px;"></div>`}
        <div style="font-size:9px;color:#999;letter-spacing:0.3px;">Principal</div>
      </div>
    </div>
  </div></div>`;
}

// Style 4 — Dark Premium
function buildIDCardDark(cfg, d, ff, dFields, sigImg, photoStyle = 'object-fit:cover;object-position:center 25%;') {
  const gold = cfg.certTitleColor || '#d4af37';
  const dark1 = '#1a1a2e';
  const dark2 = '#16213e';
  const logo = cfg.logoUrl
    ? `<img src="${cfg.logoUrl}" alt="logo" style="width:28px;height:28px;object-fit:contain;border-radius:50%;background:rgba(212,175,55,0.15);padding:2px;position:absolute;top:10px;left:10px;"/>`
    : '';
  const pSrc = d.student_photo || '';
  const photoFallback4 = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='35' r='25' fill='%23d4af37'/%3E%3Cellipse cx='50' cy='92' rx='38' ry='28' fill='%23d4af37'/%3E%3C/svg%3E`;
  const photo = pSrc
    ? `<img src="${pSrc}" style="width:100%;height:100%;${photoStyle}border-radius:50%;" onerror="this.onerror=null;this.src='${photoFallback4}';"/>`
    : `<img src="${photoFallback4}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;"/>`;
  const rows = dFields.map(f => idCardRow(f.label, d[f.key], '#aaa', f.key === 'blood_group' ? '#ff7070' : '#f0e6c8')).join('');
  return `<div style="width:100%;display:flex;justify-content:center;padding:28px 0;background:#0f0f1e;min-height:480px;box-sizing:border-box;">
  <div style="width:280px;border-radius:16px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.55);font-family:${ff};background:${dark1};position:relative;">
    <div style="background:linear-gradient(155deg,${dark2},#0f3460);padding:20px 16px 62px;position:relative;text-align:center;min-height:118px;border-bottom:1px solid ${gold}40;">
      ${logo}
      <div style="color:${gold};font-weight:900;font-size:13px;letter-spacing:1px;text-transform:uppercase;line-height:1.3;">${cfg.schoolName || 'SCHOOL NAME'}</div>
      ${cfg.address ? `<div style="color:${gold}66;font-size:9.5px;margin-top:2px;">${cfg.address}</div>` : ''}
      <div style="position:absolute;bottom:-43px;left:50%;transform:translateX(-50%);width:86px;height:86px;border-radius:50%;border:3px solid ${gold};overflow:hidden;z-index:2;background:#2a2a4a;">${photo}</div>
    </div>
    <div style="padding:50px 18px 10px;text-align:center;">
      <div style="font-size:16px;font-weight:900;color:#f0e6c8;text-transform:uppercase;letter-spacing:0.8px;">${d.student_name || 'STUDENT NAME'}</div>
      <div style="display:inline-block;border:1px solid ${gold};color:${gold};font-size:10px;font-weight:700;border-radius:4px;padding:2px 12px;margin-top:5px;letter-spacing:0.4px;">${d.class_name || 'CLASS'} &nbsp;•&nbsp; ${d.section_name || 'SECTION'}</div>
      <div style="height:1px;background:${gold}33;margin:11px 0;"></div>
      <table style="width:100%;border-collapse:collapse;text-align:left;"><tbody>${rows || `<tr><td colspan="3" style="text-align:center;color:#555;font-size:11px;padding:6px;">No fields</td></tr>`}</tbody></table>
    </div>
    <div style="padding:6px 18px 14px;display:flex;justify-content:flex-end;">
      <div style="text-align:center;min-width:90px;">
        ${sigImg ? `<img src="${sigImg}" style="max-width:86px;max-height:28px;object-fit:contain;display:block;margin:0 auto 4px;filter:invert(1);" />` : `<div style="height:30px;border-bottom:1.5px solid ${gold}55;margin-bottom:4px;"></div>`}
        <div style="font-size:10px;font-weight:600;color:${gold}99;letter-spacing:0.3px;">Principal</div>
      </div>
    </div>
    <div style="height:3px;background:linear-gradient(to right,${gold},transparent,${gold});"></div>
    <div style="height:7px;background:${dark2};"></div>
  </div></div>`;
}

// Style 5 — Green Wave (white header, rectangular photo, wave+green background image)
const ID_CARD_GREEN_FONT = "Arial, Helvetica, sans-serif";
// idcard-green-bg-body.jpg is the wave+green portion only (header cropped off),
// 638x793. Stretched to fill the body container exactly, so it always covers the
// full height regardless of how much content (photo/name/rows) it holds.
// Standard ID-card physical size (CR80, portrait): 54mm × 86mm.
const ID_CARD_GREEN_W = '54mm';
const ID_CARD_GREEN_H = '86mm';
const ID_CARD_GREEN_BODY_MINH = '52mm';

// Green Wave shows a fixed set of detail rows (the layout has no separate class
// chip, so class_name is included here even though it's an ID_SPECIAL_FIELD).
const ID_CARD_GREEN_FIELDS = [
  // "Class / Section" heading, but show the section value only (e.g. "X-A").
  { key: 'class_name',   label: 'Class / Section',
    value: d => d.section_name || '' },
  { key: 'admission_no', label: 'Admission No'  },
  { key: 'father_name',  label: 'Father Name'   },
  { key: 'dob',          label: 'Date of Birth' },
  { key: 'phone',        label: 'Phone'         },
];

function idCardRowWhite(label, value) {
  return `<tr>
    <td style="padding:2.5px 5px 2.5px 0;color:#fff;font-size:9px;font-weight:700;min-width:74px;vertical-align:top;white-space:nowrap;text-align:left;line-height:1.25;">${label}</td>
    <td style="padding:2.5px 3px;color:#fff;font-size:9px;font-weight:700;vertical-align:top;text-align:left;line-height:1.25;">:</td>
    <td style="padding:2.5px 0;font-weight:700;font-size:9px;color:#fff;word-break:break-word;text-align:left;line-height:1.25;">${value || '—'}</td>
  </tr>`;
}

// Shared white header — circular logo left, bold black uppercase school name right
function idCardGreenHeader(cfg) {
  const logo = cfg.logoUrl
    ? `<img src="${cfg.logoUrl}" alt="logo" style="width:36px;height:36px;object-fit:contain;border-radius:50%;flex-shrink:0;"/>`
    : '';
  return `<div style="padding:8px 12px 6px;display:flex;align-items:center;justify-content:center;gap:8px;background:#fff;">
    ${logo}
    <div style="flex:1;text-align:center;color:#000;font-weight:900;font-size:12px;font-family:'Arial Black',Arial,Helvetica,sans-serif;text-transform:uppercase;line-height:1.12;">${cfg.schoolName || 'SCHOOL NAME'}</div>
  </div>`;
}

// Shared wave+green background area — idcard-green-bg-body.jpg stretched to fill
// exactly, so the wave/gradient texture always covers the full height (no white
// gap or flat-color seam below it when content is taller/shorter than the source).
function idCardGreenBgStyle() {
  // Pull the body up under the white header and over-stretch the background image
  // ~4% taller than the box (aligned to the bottom) so its faint top JPEG edge is
  // clipped above the box and never shows as a seam/line below the header.
  // NOTE: use a plain percentage, NOT calc() — html2canvas (used by Print/PDF
  // rasterisation) ignores calc() in background-size, which let the edge line
  // reappear in the exported card.
  return `position:relative;width:100%;min-height:${ID_CARD_GREEN_BODY_MINH};flex:1;display:flex;flex-direction:column;margin-top:-2px;background-image:url(${idCardGreenBg});background-size:100% 104%;background-position:center bottom;background-repeat:no-repeat;`;
}

function buildIDCardGreen(cfg, d, ff, dFields, sigImg, photoStyle = 'object-fit:cover;object-position:center 20%;') {
  // Always render the FRONT design (photo placeholder + field labels + Principal),
  // even with no student data, so the template View / preview shows the front side
  // first and the back side second — instead of two back cards.
  const pSrc = d.student_photo || '';
  const photoFallback = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Crect width='100' height='120' fill='%23eee'/%3E%3Ccircle cx='50' cy='42' r='26' fill='%23ccc'/%3E%3Cellipse cx='50' cy='112' rx='40' ry='32' fill='%23ccc'/%3E%3C/svg%3E`;
  // Render the photo as a background image (cover) rather than <img object-fit:cover>:
  // html2canvas doesn't honour object-fit (it stretches/distorts the photo in the
  // downloaded image), but it does render background-size:cover correctly.
  const num = (v, def) => { const n = parseFloat(v); return Number.isFinite(n) ? n : def; };
  const gpx = num(d.photo_pos_x, 50);
  const gpy = num(d.photo_pos_y, 50);
  const gpz = num(d.photo_zoom, 1);
  const photo = pSrc
    ? `<div style="width:100%;height:100%;background-image:url('${pSrc}');background-repeat:no-repeat;background-position:${gpx}% ${gpy}%;background-size:${gpz > 1 ? (gpz * 100) + '%' : 'cover'};"></div>`
    : `<div style="width:100%;height:100%;background-image:url('${photoFallback}');background-size:cover;background-position:center;background-repeat:no-repeat;"></div>`;
  const rows = dFields.map(f => idCardRowWhite(f.label, f.value ? f.value(d) : d[f.key])).join('');

  return `<div style="width:${ID_CARD_GREEN_W};height:${ID_CARD_GREEN_H};border-radius:10px;overflow:hidden;box-shadow:0 6px 24px rgba(0,0,0,0.2);font-family:${ID_CARD_GREEN_FONT};background:#545f30;position:relative;display:flex;flex-direction:column;">
    ${idCardGreenHeader(cfg)}
    <div style="${idCardGreenBgStyle()}">
      <div style="width:82px;height:90px;margin:4px auto 0;border:2px solid #1a1a1a;background:#eee;overflow:hidden;flex-shrink:0;">${photo}</div>
      <div style="padding:5px 14px 8px;text-align:center;flex:1;display:flex;flex-direction:column;min-height:0;">
        <div style="color:#fff;font-weight:900;font-size:12px;text-transform:uppercase;letter-spacing:0.2px;padding:2px 0 3px;word-break:break-word;">${d.student_name || 'STUDENT NAME'}</div>
        <table style="width:auto;margin:0 auto;border-collapse:collapse;"><tbody>${rows || `<tr><td colspan="3" style="text-align:center;color:rgba(255,255,255,0.6);font-size:9px;padding:4px;">No fields</td></tr>`}</tbody></table>
        <div style="text-align:right;margin-top:auto;padding-top:4px;flex-shrink:0;">
          ${sigImg
            ? `<img src="${sigImg}" style="max-width:110px;max-height:22px;object-fit:contain;display:block;margin:0 0 1px auto;" />`
            : `<div style="width:80px;height:18px;border-bottom:1.5px solid rgba(255,255,255,0.7);margin:0 0 3px auto;"></div>`}
          <div style="color:#fff;font-weight:400;font-size:10px;">Principal</div>
        </div>
      </div>
    </div>
  </div>`;
}

// Back side for the Green Wave style — green header with the school name,
// wave divider, then a white body with the institute logo + address (no per-student data)
function buildIDCardGreenBack(cfg) {
  const logo = cfg.logoUrl
    ? `<img src="${cfg.logoUrl}" alt="logo" style="display:block;width:62px;height:62px;object-fit:contain;border-radius:50%;margin:-56px auto 0;"/>`
    : '';
  // width:100% + text-align:center set directly on each text block (rather than
  // relying solely on the parent's flex align-items:center to shrink-and-centre
  // them) — html2canvas (used by Print/PDF) renders nested flex cross-axis
  // centering unreliably, which left this text hugging the left edge when printed.
  // The address field is free text and often has phone numbers typed straight
  // after the 6-digit PIN code (e.g. "...Telangana 500064 7569761658 / 8522943573") —
  // split anything after the PIN onto its own line instead of running them together.
  const addrSplit  = /^(.*?\b\d{6}\b),?\s*(.*)$/.exec((cfg.address || '').trim());
  const addressMain  = addrSplit ? addrSplit[1].trim() : (cfg.address || '').trim();
  const addressPhone = addrSplit ? addrSplit[2].trim() : '';

  const address = addressMain
    ? `<div style="width:100%;margin-top:14px;color:#1a1a1a;font-size:11.5px;font-weight:600;line-height:1.6;word-break:break-word;text-align:center;">${addressMain}</div>`
    : '';
  const addressPhoneLine = addressPhone
    ? `<div style="width:100%;margin-top:4px;color:#1a1a1a;font-size:11.5px;font-weight:600;line-height:1.6;text-align:center;">${addressPhone}</div>`
    : '';
  const phone = cfg.phone
    ? `<div style="width:100%;margin-top:4px;color:#1a1a1a;font-size:11.5px;font-weight:600;line-height:1.6;text-align:center;">Phone: ${cfg.phone}</div>`
    : '';

  return `<div style="width:${ID_CARD_GREEN_W};height:${ID_CARD_GREEN_H};border-radius:10px;overflow:hidden;box-shadow:0 6px 24px rgba(0,0,0,0.2);font-family:${ID_CARD_GREEN_FONT};background:#fff;position:relative;display:flex;flex-direction:column;">
    <div style="background:#545f30;padding:46px 16px 12px;text-align:center;">
      <div style="color:#fff;font-weight:900;font-size:18px;text-transform:uppercase;line-height:1.3;word-break:break-word;">${cfg.schoolName || 'SCHOOL NAME'}</div>
    </div>
    <img src="${idCardBackWave}" alt="" style="display:block;width:100%;height:88px;margin-bottom:18px;"/>
    <div style="width:100%;flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:8px 18px 22px;text-align:center;">
      ${logo}
      ${address}
      ${addressPhoneLine}
      ${phone}
    </div>
  </div>`;
}

// ── ID Card renderer — dispatches to style ────────────────────────────────

function buildIDCardHTML(cfg, data, signatureImages = {}) {
  // ID card renders values directly (no replaceVariables), so normalise any
  // ISO date value (YYYY-MM-DD) to DD/MM/YYYY for display here.
  const fmtIdDate = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
  };
  const d = {};
  Object.entries(data || {}).forEach(([k, v]) => { d[k] = (v == null) ? v : fmtIdDate(v); });
  const ff      = cfg.fontFamily || 'Arial, Helvetica, sans-serif';
  const style   = cfg.idCardStyle || 'wave';
  const dFields = getIDCardDetailFields(cfg);
  // Generation-time upload wins; otherwise fall back to the signature baked into
  // the template (ID Card Design → Principal Signature).
  const sigImg  = signatureImages[0] || cfg.idCardSignature || null;

  // Manual photo adjustment — pan via transform translate (works on BOTH axes
  // regardless of photo aspect ratio) + zoom via scale. Driven by
  // photo_pos_x / photo_pos_y (0–100, 50 = centred) and photo_zoom (1–3).
  const num = (v, def) => { const n = parseFloat(v); return Number.isFinite(n) ? n : def; };
  const px = num(d.photo_pos_x, 50);
  const py = num(d.photo_pos_y, 50);
  const pz = num(d.photo_zoom, 1);
  // 50 → 0% shift (centred); 0 → +50% (reveal left/top); 100 → -50% (reveal right/bottom)
  const tx = (50 - px).toFixed(1);
  const ty = (50 - py).toFixed(1);
  const photoStyle = `object-fit:cover;transform:translate(${tx}%, ${ty}%) scale(${pz});transform-origin:center;`;

  if (style === 'sidebar') return `<div id="dcb-idcard-front">${buildIDCardSidebar(cfg, d, ff, dFields, sigImg, photoStyle)}</div>`;
  if (style === 'formal')  return `<div id="dcb-idcard-front">${buildIDCardFormal(cfg, d, ff, dFields, sigImg, photoStyle)}</div>`;
  if (style === 'dark')    return `<div id="dcb-idcard-front">${buildIDCardDark(cfg, d, ff, dFields, sigImg, photoStyle)}</div>`;
  if (style === 'green') {
    // Green Wave uses its own fixed field set (Class, Admission No, Father Name, DOB, Phone).
    return `<div style="width:100%;display:flex;justify-content:center;align-items:flex-start;gap:24px;padding:28px 0;background:#f0f0f0;min-height:480px;box-sizing:border-box;flex-wrap:wrap;">
      <div id="dcb-idcard-front">${buildIDCardGreen(cfg, d, ff, ID_CARD_GREEN_FIELDS, sigImg, photoStyle)}</div>
      <div id="dcb-idcard-back">${buildIDCardGreenBack(cfg)}</div>
    </div>`;
  }
  return `<div id="dcb-idcard-front">${buildIDCardWave(cfg, d, ff, dFields, sigImg, photoStyle)}</div>`;
}

// ── Dynamic field-driven body (single source for table-based certs) ──────
// Builds the certificate body straight from cfg.fields so the builder preview,
// certificate-creation, view and the downloaded file all render identically:
//   • header fields (Admission No / Issue Date …) in a row at the top
//   • remaining visible fields as numbered "Label : value" rows, in field order
// Empty/missing values show "-". tc_no / serial_no are collected but never
// displayed (HEADER_DISPLAY_SKIP). With empty data this naturally yields the
// "name : -" skeleton used as the builder preview.
const HEADER_DISPLAY_SKIP = new Set(['tc_no', 'serial_no']);

const fmtCertDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
};

const dynamicCellValue = (data, key) => {
  const v = data ? data[key] : undefined;
  if (v == null || String(v).trim() === '') return '-';
  return fmtCertDate(v);
};

function buildDynamicTableBody(cfg, data = {}, hiddenKeys = []) {
  const headerKeys = getHeaderFieldKeys(cfg.certType);
  const hidden  = new Set(hiddenKeys);
  const visible = (cfg.fields || []).filter(f => f.visible && !hidden.has(f.key));
  if (visible.length === 0) {
    return `<div style="padding:24px;text-align:center;color:#aaa;font-size:13px;">No visible fields</div>`;
  }

  const headerFields = headerKeys.length
    ? headerKeys
        .filter(k => !HEADER_DISPLAY_SKIP.has(k))
        .map(k => visible.find(f => f.key === k))
        .filter(Boolean)
    : [];
  const bodyFields = visible.filter(f => !headerKeys.includes(f.key));

  // UDISE No. (school_code) is a fixed institution value held on the template,
  // not per-student data — pull it from cfg.schoolCode (data still wins if present).
  const headerCellValue = (f) => {
    if (f.key === 'school_code') {
      const v = (data && data.school_code) || cfg.schoolCode;
      return (v == null || String(v).trim() === '') ? '-' : v;
    }
    return dynamicCellValue(data, f.key);
  };

  // Force the renamed UDISE label even on older templates saved as "School Code".
  const headerLabel = (f) => (f.key === 'school_code' ? 'UDISE No.' : f.label);

  const headerHTML = headerFields.length
    ? `<div style="display:flex;flex-wrap:wrap;gap:10px 24px;justify-content:space-between;margin-bottom:14px;font-size:12px;">` +
      headerFields.map(f => `<span>${headerLabel(f)}:&nbsp;<b>${headerCellValue(f)}</b></span>`).join('') +
      `</div>`
    : '';

  // Record Sheet packs many fields, so render it compact to fit a single A4 page
  // (smaller row padding / font); other table types keep the roomier sizing.
  const isRecord  = cfg.certType === 'recordsheet';
  const cellPad   = isRecord ? '7.1px 10px' : '6px 8px';
  const tableFont = isRecord ? 12.5 : 13;

  const rows = bodyFields.map((f, i) =>
    `<tr>` +
    `<td style="padding:${cellPad};width:50%;border:1px solid #ccc;">${i + 1}.&nbsp;${f.label}</td>` +
    `<td style="padding:${cellPad};width:50%;border:1px solid #ccc;"><b>${dynamicCellValue(data, f.key)}</b></td>` +
    `</tr>`
  ).join('\n');
  const table = bodyFields.length
    ? `<table style="width:100%;border-collapse:collapse;font-size:${tableFont}px;border-top:1px solid #999;">\n${rows}\n</table>`
    : '';

  return `<div>${headerHTML}${table}</div>`;
}

// ── Record Sheet body builder (used by builder + create-cert tab) ────────

export function buildRecordSheetBody(fields = [], withBorder = true) {
  const tableStyle = withBorder
    ? 'width:100%;border-collapse:collapse;font-size:13px;border-top:1px solid #888;'
    : 'width:100%;border-collapse:collapse;font-size:13px;';
  const header = `<div style="padding:6px 0;font-family:'Times New Roman',serif;">
  <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:10px;">
    <span>Date: <u><b>{{date}}</b></u></span>
    <span>UDISE No.: <u><b>{{school_code}}</b></u></span>
  </div>
  <table style="${tableStyle}">`;
  const visible = fields.filter(f => f.visible);
  const rows = visible.map((f, i) => {
    const num = `${i + 1}.`;
    return withBorder
      ? `    <tr><td style="padding:5px 8px;width:5%;border:1px solid #ccc;">${num}</td>` +
        `<td style="padding:5px 8px;width:52%;border:1px solid #ccc;">${f.label}</td>` +
        `<td style="padding:5px 8px;border:1px solid #ccc;"><b>{{${f.key}}}</b></td></tr>`
      : `    <tr><td style="padding:5px 8px;width:5%;">${num}</td>` +
        `<td style="padding:5px 8px;width:52%;">${f.label}</td>` +
        `<td style="padding:5px 8px;width:20px;text-align:center;">:</td>` +
        `<td style="padding:5px 8px;"><b>{{${f.key}}}</b></td></tr>`;
  });
  return `${header}\n${rows.join('\n')}\n  </table>\n</div>`;
}

// ── Inline remote images as base64 before html2canvas ───────────────────
// html2canvas renders cross-origin <img> blank (the canvas is tainted), so the
// institution logo / watermark disappear in the downloaded PDF. The logo host
// (Google Cloud Storage) sends no CORS headers, so a direct fetch is blocked
// too — we route remote images through a CORS-enabled image proxy, fetch the
// bytes, and swap in a base64 data URL, restoring the originals afterwards.
const PROXY_BASE = 'https://images.weserv.nl/?url=';

const proxiedUrl = (src) => {
  const stripped = src.replace(/^https?:\/\//i, '');
  const prefix = /^https:/i.test(src) ? 'ssl:' : '';
  // output=png preserves logo transparency.
  return `${PROXY_BASE}${encodeURIComponent(prefix + stripped)}&output=png`;
};

const blobToDataURL = (blob) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(fr.result);
  fr.onerror = reject;
  fr.readAsDataURL(blob);
});

const fetchAsDataURL = async (url) => {
  const res = await fetch(url, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  const blob = await res.blob();
  if (!blob || blob.size === 0) throw new Error('empty image');
  return blobToDataURL(blob);
};

export async function inlineImagesForCapture(root) {
  const imgs = Array.from(root.querySelectorAll('img'));
  const originals = imgs.map(img => img.getAttribute('src'));

  await Promise.all(imgs.map(async (img, i) => {
    const src = originals[i];
    if (!src || src.startsWith('data:')) return;

    const isAbsolute = /^https?:\/\//i.test(src);
    const sameOrigin = !isAbsolute || src.startsWith(window.location.origin);

    let dataUrl = null;
    // Same-origin: fetch directly. Cross-origin: go straight through the proxy
    // (the logo host has no CORS), with a direct fetch as a last-ditch fallback.
    try {
      dataUrl = await fetchAsDataURL(sameOrigin ? src : proxiedUrl(src));
    } catch {
      try { dataUrl = await fetchAsDataURL(sameOrigin ? proxiedUrl(src) : src); }
      catch { /* leave original src untouched */ }
    }
    if (!dataUrl) return;

    await new Promise((resolve) => {
      img.onload = img.onerror = resolve;
      img.setAttribute('src', dataUrl);
    });
  }));

  // Restore the original srcs once capture is done.
  return () => imgs.forEach((img, i) => {
    if (originals[i] != null) img.setAttribute('src', originals[i]);
  });
}

// ── HTML generator (pure function) ──────────────────────────────────────

export function buildCertHTML(cfg, data, hiddenKeys = [], signatureImages = {}) {
  if (cfg.certType === 'idcard') return buildIDCardHTML(cfg, data, signatureImages);

  const fontFamily = cfg.fontFamily || "'Times New Roman', Times, serif";

  const bStyle = cfg.borderImageUrl
    ? `${cfg.borderThickness || 20}px solid transparent`
    : cfg.borderStyle === 'none'
      ? 'none'
      : `${cfg.borderThickness}px ${cfg.borderStyle} ${cfg.borderColor}`;

  const borderImageCSS = cfg.borderImageUrl
    ? `border-image:url('${cfg.borderImageUrl}') 30% stretch;`
    : '';

  let bgCSS = 'background:#fff;';
  if (cfg.bgType === 'color')
    bgCSS = `background:${cfg.bgColor};`;
  else if (cfg.bgType === 'gradient')
    bgCSS = `background:linear-gradient(${cfg.bgGradientDir},${cfg.bgGradientStart},${cfg.bgGradientEnd});`;
  else if (cfg.bgType === 'image' && cfg.bgImageUrl)
    bgCSS = `background:url('${cfg.bgImageUrl}') center/cover no-repeat;`;

  const containerStyle = [
    'position:relative',
    'width:100%',
    'box-sizing:border-box',
    `border:${bStyle}`,
    borderImageCSS,
    `border-radius:${cfg.borderRadius}px`,
    `font-family:${fontFamily}`,
    'color:#000',
    bgCSS,
  ].filter(Boolean).join(';');

  const fontOverrideStyle = `<style>.dcb-cert-root,.dcb-cert-root *{font-family:${fontFamily}!important;}.dcb-cert-root p,.dcb-cert-root div,.dcb-cert-root span{text-decoration:none;}</style>`;

  const innerDivStyle = cfg.innerBorder && !cfg.borderImageUrl
    ? `position:absolute;top:${cfg.innerBorderInset}px;left:${cfg.innerBorderInset}px;` +
      `right:${cfg.innerBorderInset}px;bottom:${cfg.innerBorderInset}px;` +
      `border:${cfg.innerBorderThickness}px solid ${cfg.innerBorderColor};` +
      `pointer-events:none;z-index:0;`
    : null;

  // Record Sheet uses a larger watermark than the other certificate types.
  const watermarkWidth = cfg.certType === 'recordsheet' ? 80 : 55;
  const watermarkHTML = cfg.watermarkUrl
    ? `<div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);` +
      `width:${watermarkWidth}%;opacity:${cfg.watermarkOpacity};pointer-events:none;z-index:0;text-align:center;">` +
      `<img src="${cfg.watermarkUrl}" style="width:100%;height:auto;" /></div>`
    : '';

  // Record Sheet uses a larger logo than the other certificate types.
  const logoSize = cfg.certType === 'recordsheet' ? 95 : 72;
  const logoHTML = cfg.logoUrl
    ? `<img src="${cfg.logoUrl}" alt="logo" ` +
      `style="width:${logoSize}px;height:${logoSize}px;object-fit:contain;flex-shrink:0;" />`
    : '';

  const headerHTML = `
    <div style="display:flex;align-items:flex-start;gap:14px;padding:${cfg.certType === 'recordsheet' ? '4px 32px 4px' : '20px 32px 10px'};">
      ${logoHTML}
      <div style="flex:1;text-align:${cfg.alignment};">
        <div style="font-size:${cfg.schoolNameSize}px;font-weight:900;color:${cfg.schoolNameColor};
          text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px;">
          ${cfg.schoolName || 'INSTITUTION NAME'}
        </div>
        <div style="font-size:12px;font-style:italic;color:#444;">${cfg.recognitionText}</div>
        ${cfg.address ? `<div style="font-size:12px;color:#444;">${cfg.address}</div>` : ''}
        ${cfg.phone   ? `<div style="font-size:12px;color:#444;">Ph: ${cfg.phone}</div>`   : ''}
      </div>
    </div>`;

  const isBonafide = cfg.certType === 'bonafide';
  const isStudy = cfg.certType === 'study';
  const isRecord = cfg.certType === 'recordsheet';
  const isStructured = isBonafide || isStudy;
  const titleHTML  = isBonafide
    ? `<div style="display:flex;justify-content:center;padding:4px 0 10px;">
         <div style="background:linear-gradient(135deg,${cfg.certTitleColor},#283593);color:#fff;
           padding:7px 34px;border-radius:24px;font-size:${cfg.certTitleSize}px;
           font-weight:800;letter-spacing:3px;">${cfg.certTitle}</div>
       </div>`
    : isStudy
    ? `<div style="display:flex;justify-content:center;padding:4px 0 10px;">
         <div style="background:linear-gradient(135deg,${cfg.certTitleColor},#283593);color:#fff;
           padding:7px 34px;border-radius:24px;font-size:${cfg.certTitleSize}px;
           font-weight:800;letter-spacing:3px;">${cfg.certTitle}</div>
       </div>`
    : `<div style="text-align:center;padding:${isRecord ? '3px 32px' : '6px 32px'};margin-bottom:${isRecord ? '2px' : '8px'};
         border-top:1px solid #ccc;border-bottom:1px solid #ccc;">
         <span style="font-size:${cfg.certTitleSize}px;font-weight:800;letter-spacing:2.5px;
           text-decoration:underline;color:${cfg.certTitleColor};">${cfg.certTitle}</span>
       </div>`;

  const cornerHTML = '';

  // Hidden field keys from cfg.fields + the explicit hiddenKeys arg
  const cfgHidden = cfg.fields
    ? cfg.fields.filter(f => !f.visible).map(f => f.key)
    : [];
  const allHidden = [...new Set([...cfgHidden, ...hiddenKeys])];

  // Bonafide / study use the structured line editor. Every other (table-based)
  // type — TC / record sheet / conduct — renders dynamically from cfg.fields so
  // the builder preview, certificate creation, view and downloaded file are all
  // identical (cfg.bodyContent is no longer the source for these types).
  const bodyHTML = isStructured
    ? replaceVariables(generateBonafideBodyHTML(cfg), data || {}, allHidden)
    : buildDynamicTableBody(cfg, data || {}, allHidden);

  // Record Sheet uses tighter signature spacing/size so it stays on one A4 page.
  const sigPad     = isRecord ? '6px 48px 26px' : isStructured ? '2px 48px 28px' : '10px 48px 40px';
  const sigFont    = isRecord ? 13 : 13;
  const sigLineH   = isRecord ? 40 : 44;
  const sigImgMaxH = isRecord ? 38 : 40;
  const renderSigBlock = (sig, idx) => {
    const imgSrc = signatureImages[idx];
    const imgHtml = imgSrc
      ? `<img src="${imgSrc}" style="max-width:110px;max-height:${sigImgMaxH}px;object-fit:contain;display:block;margin:0 auto 4px;" />`
      : `<div style="height:${sigLineH}px;border-bottom:1.5px solid #333;margin-bottom:6px;"></div>`;
    return `<div style="text-align:center;min-width:130px;">${imgHtml}<div>${sig.label}</div></div>`;
  };
  const sigOuter = `padding:${sigPad};font-size:${sigFont}px;font-weight:600;font-family:'Times New Roman',serif;`;
  // Each signature block is anchored to its chosen side (left/center/right) via
  // three fixed slots, so removing one block never reflows the others. Legacy
  // configs without an `align` value keep the old evenly-spaced layout.
  const hasAlign = (cfg.signatures || []).some(s => s && s.align);
  const sigsHTML = !(cfg.signatures || []).length
    ? ''
    : !hasAlign
      ? `<div style="display:flex;justify-content:space-between;${sigOuter}">${cfg.signatures.map(renderSigBlock).join('')}</div>`
      : (() => {
          const slot = (pos, justify) =>
            `<div style="flex:1;display:flex;gap:24px;align-items:flex-end;justify-content:${justify};">${
              cfg.signatures
                .map((s, i) => ({ s, i }))
                .filter(({ s }) => (s.align || 'left') === pos)
                .map(({ s, i }) => renderSigBlock(s, i))
                .join('')
            }</div>`;
          return `<div style="display:flex;${sigOuter}">${slot('left', 'flex-start')}${slot('center', 'center')}${slot('right', 'flex-end')}</div>`;
        })();

  const footerHTML = (() => {
    if (!cfg.footerEnabled || !cfg.footerText) return '';
    const divider = cfg.footerDivider
      ? `<hr style="margin:0 0 6px;border:none;border-top:1px solid #ccc;" />`
      : '';
    return `<div style="padding:6px 32px 14px;background:${cfg.footerBg || 'transparent'};">
      ${divider}
      <div style="font-size:${cfg.footerFontSize}px;color:${cfg.footerColor};
        text-align:${cfg.footerAlignment};font-family:'Times New Roman',serif;">
        ${cfg.footerText}
      </div>
    </div>`;
  })();

  return `
    ${fontOverrideStyle}
    <div class="dcb-cert-root" style="${containerStyle}">
      ${cornerHTML}
      ${innerDivStyle ? `<div style="${innerDivStyle}"></div>` : ''}
      ${watermarkHTML}
      <div style="position:relative;z-index:1;${isStructured ? `padding:${(cfg.innerBorderInset || 8) + 6}px;` : ''}">
        ${headerHTML}
        ${cfg.certType === 'recordsheet' && (cfg.medium_label || cfg.edu_type)
          ? `<div style="display:flex;justify-content:space-between;align-items:center;padding:5px 32px;font-size:13px;font-weight:bold;font-family:'Times New Roman',serif;">
               <span>${cfg.medium_label || ''}</span>
               <span>${cfg.edu_type || ''}</span>
             </div>`
          : ''}
        <hr style="margin:0;border:none;border-top:1.5px solid #ccc;" />
        ${titleHTML}
        <div style="padding:${isStructured ? '8px 32px 0' : isRecord ? '12px 32px' : '10px 32px'};font-size:${cfg.bodyFontSize}px;${isStructured ? '' : `line-height:${isRecord ? 1.5 : cfg.bodyLineHeight};`}">
          ${bodyHTML}
        </div>
        ${sigsHTML}
        ${footerHTML}
      </div>
    </div>`;
}

function buildCertCSS(cfg) {
  const fontFamily = cfg.fontFamily || "'Times New Roman', Times, serif";
  return (
    `.certificate,.certificate *{font-family:${fontFamily}!important;}` +
    `.certificate{color:#000;` +
    `border:${cfg.borderThickness}px ${cfg.borderStyle} ${cfg.borderColor};` +
    `border-radius:${cfg.borderRadius}px;}` +
    `.cert-header{text-align:${cfg.alignment};padding:20px 32px 10px;}` +
    `.cert-title{font-size:${cfg.certTitleSize}px;font-weight:800;color:${cfg.certTitleColor};}` +
    `.cert-body{font-size:${cfg.bodyFontSize}px;line-height:${cfg.bodyLineHeight};padding:10px 32px;}` +
    `.cert-sigs{display:flex;justify-content:space-between;padding:40px 48px 24px;}`
  );
}

// ── Bonafide structured editor helpers ──────────────────────────────────

const escapeHtml = (str) =>
  String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function getDefaultBonafideLines() {
  return [
    {
      id: 'l1',
      parts: [
        { type: 'text',  content: 'This is to Certify that Master / Miss ' },
        { type: 'blank', variable: 'student_name', width: 200 },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
    {
      id: 'l2',
      parts: [
        { type: 'text',  content: 'Son/Daughter of ' },
        { type: 'blank', variable: 'father_name', width: 180 },
        { type: 'text',  content: ' is a Bonafide Student of this' },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
    {
      id: 'l3',
      parts: [
        { type: 'text',  content: 'Institution studying in Class ' },
        { type: 'blank', variable: 'class_name', width: 120 },
        { type: 'text',  content: ' during the year ' },
        { type: 'blank', variable: 'academic_year', width: 90 },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
    {
      id: 'l4',
      parts: [
        { type: 'text',  content: 'His / Her Date of Birth as per record is ' },
        { type: 'blank', variable: 'dob', width: 180 },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
  ];
}

export function getDefaultStudyLines() {
  // Text matches physical Study Certificate format (image reference)
  return [
    {
      id: 'l1',
      parts: [
        { type: 'text',  content: 'This is to Certify that Master / Miss ' },
        { type: 'blank', variable: 'student_name', width: 200 },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
    {
      id: 'l2',
      parts: [
        { type: 'text',  content: 'Son/Daughter of ' },
        { type: 'blank', variable: 'father_name', width: 180 },
        { type: 'text',  content: ' / was a Bonafide Student of this' },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
    {
      id: 'l3',
      parts: [
        { type: 'text',  content: 'Institution studying / studied in Class ' },
        { type: 'blank', variable: 'class_name', width: 120 },
        { type: 'text',  content: ' during the year ' },
        { type: 'blank', variable: 'academic_year', width: 90 },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
    {
      id: 'l4',
      parts: [
        { type: 'text',  content: 'His / Her Date of Birth as per record is ' },
        { type: 'blank', variable: 'dob', width: 180 },
      ],
      fontStyle: 'italic', fontSize: 14,
    },
  ];
}

const getDefaultLinesForType = (type) =>
  type === 'study' ? getDefaultStudyLines() : getDefaultBonafideLines();

const buildAdmDateHTML = (certType) => {
  const borderTop = (certType === 'study' || certType === 'bonafide') ? '' : 'border-top:1px solid #c5cae9;';
  return `<div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0 12px;${borderTop}font-size:13.5px;">
    <span>Admn. No.&nbsp;&nbsp;<span style="border-bottom:1.5px solid #000;font-weight:600;font-style:normal;padding:0 30px 2px;">{{admission_no}}</span></span>
    <span>Date :&nbsp;<span style="border-bottom:1px dotted #555;padding:0 35px 2px;">{{date}}</span></span>
  </div>`;
};

export function generateBonafideBodyHTML(cfg) {
  const lines         = cfg.bonafideLines          || getDefaultLinesForType(cfg.certType);
  const conductOn     = cfg.bonafideConductEnabled !== false;
  const conductText   = cfg.bonafideConductText    || 'His / Her Character & Conduct found to be good.';
  const conductSize   = cfg.bonafideConductSize    || 15;
  const footerLines   = cfg.bonafideFooterLines    || [];

  const linesHTML = lines.map(line => {
    const fs  = line.fontStyle || 'italic';
    const fsz = line.fontSize  || 14;
    const parts = line.parts;
    const partsHTML = parts.map((p, idx) => {
      const isLast = idx === parts.length - 1;
      if (p.type === 'text') {
        return `<span>${escapeHtml(p.content)}</span>`;
      }
      const pad = Math.round((p.width || 100) / 4);
      if (p.variable === '_blank_') {
        return `<span style="border-bottom:1.5px solid #000;padding:0 ${pad}px 2px;display:inline-block;min-width:${p.width || 100}px;">&nbsp;</span>`;
      }
      return `<span style="display:inline-block;white-space:nowrap;vertical-align:bottom;border-bottom:1.5px solid #000;padding:0 ${pad}px 2px;font-weight:600;font-style:normal;">{{${p.variable}}}</span>`;
    }).join('');
    return `<span style="font-style:${fs};font-size:${fsz}px;line-height:1.9 !important;">${partsHTML}&nbsp;</span>`;
  }).join('');

  const conductHTML = conductOn
    ? `\n    <div style="text-align:center;font-weight:bold;font-size:${conductSize}px;font-style:normal;margin-top:8px;">${escapeHtml(conductText)}</div>`
    : '';

  const footerHTML = footerLines.length > 0
    ? '\n    ' + footerLines.map(fl =>
        `<div style="font-size:${fl.fontSize || 13}px;font-style:${fl.fontStyle || 'normal'};text-align:${fl.align || 'left'};margin-top:4px;">${escapeHtml(fl.content)}</div>`
      ).join('\n    ')
    : '';

  return `<div style="font-family:'Times New Roman',serif;">
  ${buildAdmDateHTML(cfg.certType)}
  <div style="padding:6px 0 0;">
    <p style="line-height:1.9 !important;margin:0 0 6px 0;">${linesHTML}</p>${conductHTML}${footerHTML}
  </div>
</div>`;
}

// ── Bonafide Line Editor component ───────────────────────────────────────

const BF_VARS = [
  { key: '_blank_',       label: '— Leave Blank —'  },
  { key: 'student_name',  label: 'Student Name'  },
  { key: 'father_name',   label: 'Father Name'   },
  { key: 'mother_name',   label: 'Mother Name'   },
  { key: 'admission_no',  label: 'Admission No'  },
  { key: 'class_name',    label: 'Class'         },
  { key: 'section_name',  label: 'Section'       },
  { key: 'academic_year', label: 'Academic Year' },
  { key: 'dob',           label: 'Date of Birth' },
  { key: 'date',          label: 'Date'          },
  { key: 'roll_no',       label: 'Roll No'       },
];

const BonafideLineEditor = ({ cfg, onUpdate }) => {
  const lines       = cfg.bonafideLines          || getDefaultLinesForType(cfg.certType);
  const conductOn   = cfg.bonafideConductEnabled !== false;
  const conductText = cfg.bonafideConductText    || 'His / Her Character & Conduct found to be good.';
  const conductSize = cfg.bonafideConductSize    || 15;
  const footerLines = cfg.bonafideFooterLines    || [];

  const push = (patch) => {
    const merged = { ...cfg, ...patch };
    onUpdate({ ...patch, bodyContent: generateBonafideBodyHTML(merged) });
  };

  const updLine = (id, lp) =>
    push({ bonafideLines: lines.map(l => l.id === id ? { ...l, ...lp } : l) });

  const updPart = (lid, pi, pp) =>
    push({
      bonafideLines: lines.map(l =>
        l.id !== lid ? l : { ...l, parts: l.parts.map((p, i) => i === pi ? { ...p, ...pp } : p) }
      ),
    });

  const addPart = (lid, part) =>
    push({ bonafideLines: lines.map(l => l.id !== lid ? l : { ...l, parts: [...l.parts, part] }) });

  const rmPart = (lid, pi) =>
    push({ bonafideLines: lines.map(l => l.id !== lid ? l : { ...l, parts: l.parts.filter((_, i) => i !== pi) }) });

  const delLine = (id) => push({ bonafideLines: lines.filter(l => l.id !== id) });

  const addLine = () =>
    push({
      bonafideLines: [...lines, {
        id: `c_${Date.now()}`,
        parts: [{ type: 'text', content: 'Custom text ' }, { type: 'blank', variable: '_blank_', width: 150 }],
        fontStyle: 'normal', fontSize: 14,
      }],
    });

  const updFooter = (nf) => push({ bonafideFooterLines: nf });

  return (
    <div className="bfe-root">
      <p className="dcb-hint">Each line has text segments + blank (underline) fields. Changes update the preview instantly.</p>

      {lines.map((line, li) => (
        <div key={line.id} className="bfe-line">
          <div className="bfe-line-hdr">
            <span className="bfe-line-num">Line {li + 1}</span>
            <div className="bfe-line-style">
              <select className="bfe-sel"
                value={line.fontStyle}
                onChange={e => updLine(line.id, { fontStyle: e.target.value })}
              >
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
                <option value="bold">Bold</option>
                <option value="bold italic">Bold Italic</option>
              </select>
              <div className="bfe-num-wrap">
                <input type="number" min={10} max={24} value={line.fontSize}
                  onChange={e => updLine(line.id, { fontSize: +e.target.value })} />
                <span>px</span>
              </div>
            </div>
            <button className="bfe-del-line" onClick={() => delLine(line.id)} title="Delete line">✕</button>
          </div>

          <div className="bfe-parts">
            {line.parts.map((part, pi) => (
              <div key={pi} className={`bfe-part bfe-part--${part.type}`}>
                {part.type === 'text' ? (
                  <>
                    <span className="bfe-part-badge">T</span>
                    <input className="bfe-part-txt"
                      value={part.content}
                      onChange={e => updPart(line.id, pi, { content: e.target.value })}
                      placeholder="Label text"
                    />
                  </>
                ) : (
                  <>
                    <span className="bfe-part-badge bfe-part-badge--blank">___</span>
                    <select className="bfe-sel bfe-part-var"
                      value={part.variable}
                      onChange={e => updPart(line.id, pi, { variable: e.target.value })}
                    >
                      {BF_VARS.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                    </select>
                    <div className="bfe-num-wrap">
                      <input type="number" min={60} max={400} value={part.width || 150}
                        onChange={e => updPart(line.id, pi, { width: +e.target.value })} />
                      <span>px</span>
                    </div>
                  </>
                )}
                <button className="bfe-part-rm" onClick={() => rmPart(line.id, pi)}>×</button>
              </div>
            ))}
          </div>

          <div className="bfe-add-parts">
            <button className="bfe-add-part bfe-add-part--text"
              onClick={() => addPart(line.id, { type: 'text', content: ' text ' })}>
              + Text
            </button>
            <button className="bfe-add-part bfe-add-part--blank"
              onClick={() => addPart(line.id, { type: 'blank', variable: 'student_name', width: 150 })}>
              + Blank (_____)
            </button>
          </div>
        </div>
      ))}

      <button className="bfe-add-line-btn" onClick={addLine}>+ Add Custom Line</button>

      {/* Conduct line */}
      <div className="bfe-conduct-box">
        <div className="bfe-box-hdr">
          <span className="bfe-box-label">Conduct Line</span>
          <label className="dcb-toggle">
            <input type="checkbox" checked={conductOn}
              onChange={e => push({ bonafideConductEnabled: e.target.checked })} />
            <span className="dcb-toggle-track" />
          </label>
        </div>
        {conductOn && (
          <div className="bfe-conduct-body">
            <input className="bfe-full-input"
              value={conductText}
              onChange={e => push({ bonafideConductText: e.target.value })}
              placeholder="e.g. His / Her Character & Conduct found to be good."
            />
            <div className="bfe-num-wrap" style={{ marginTop: 4 }}>
              <input type="number" min={10} max={22} value={conductSize}
                onChange={e => push({ bonafideConductSize: +e.target.value })} />
              <span>px</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer text lines */}
      <div className="bfe-footer-box">
        <div className="bfe-box-hdr">
          <span className="bfe-box-label">Footer Text</span>
          <button className="bfe-add-part bfe-add-part--text"
            onClick={() => updFooter([...footerLines, { content: '', fontSize: 13, fontStyle: 'normal', align: 'left' }])}>
            + Add Line
          </button>
        </div>
        {footerLines.map((fl, idx) => (
          <div key={idx} className="bfe-footer-row">
            <input className="bfe-part-txt"
              value={fl.content}
              onChange={e => updFooter(footerLines.map((f, i) => i === idx ? { ...f, content: e.target.value } : f))}
              placeholder="Footer text"
            />
            <select className="bfe-sel"
              value={fl.fontStyle || 'normal'}
              onChange={e => updFooter(footerLines.map((f, i) => i === idx ? { ...f, fontStyle: e.target.value } : f))}
            >
              <option value="normal">Normal</option>
              <option value="italic">Italic</option>
              <option value="bold">Bold</option>
            </select>
            <div className="bfe-num-wrap">
              <input type="number" min={10} max={20} value={fl.fontSize || 13}
                onChange={e => updFooter(footerLines.map((f, i) => i === idx ? { ...f, fontSize: +e.target.value } : f))} />
              <span>px</span>
            </div>
            <button className="bfe-part-rm"
              onClick={() => updFooter(footerLines.filter((_, i) => i !== idx))}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Normalize backend certificate_type → preset key ─────────────────────

const CERT_TYPE_NORMALIZE = {
  'tc':                   'tc',
  'transfer certificate': 'tc',
  'transfer':             'tc',
  'bonafide':             'bonafide',
  'bonafide certificate': 'bonafide',
  'study':                'study',
  'study certificate':    'study',
  'record sheet':         'recordsheet',
  'recordsheet':          'recordsheet',
  'record':               'recordsheet',
  'student id':           'idcard',
  'student id card':      'idcard',
  'id card':              'idcard',
  'idcard':               'idcard',
  'conduct':              'conduct',
};

const normalizeCertType = (raw) =>
  CERT_TYPE_NORMALIZE[raw.toLowerCase()] || raw.toLowerCase();

// ── Max fields limit ─────────────────────────────────────────────────────
const MAX_FIELDS = 40;

// ── Main component ───────────────────────────────────────────────────────

const DynamicCertificateBuilder = ({ initialTemplate = null, onTemplateSaved, saveTemplate, updateTemplate, branches = [], certificateTypes = [], savedTemplates = [] }) => {
  const previewRef  = useRef(null);
  const bodyTARef   = useRef(null);
  const scrollRef   = useRef(null);
  const [a4Scale, setA4Scale] = useState(0.4);

  const [cfg, setCfg] = useState(() => {
    if (initialTemplate) {
      try {
        return JSON.parse(initialTemplate.template_json);
      } catch { /* fallthrough */ }
    }
    return DEFAULT_CFG;
  });

  const set = useCallback((key, val) => {
    setCfg(c => ({ ...c, [key]: val }));
    setTemplateSaved(false);
  }, []);

  // Remembers the exact index of each deleted idcard field so re-adding restores it
  const deletedIdcardPositions = useRef({});

  // When editing an existing template, start as unsaved so the Save button is active
  const [templateSaved, setTemplateSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const [sections, setSections] = useState({
    type: true, header: true, idcard: true, fields: true, variables: false,
    bonafide: true, body: false, border: false, background: false, signatures: false, footer: false,
  });
  const toggle = key => setSections(s => ({ ...s, [key]: !s[key] }));

  const [showLibrary, setShowLibrary] = useState(true);
  const [saveMsg,     setSaveMsg]     = useState('');
  const [showErrors,  setShowErrors]  = useState(false);

  // ── Field editing state ──────────────────────────────────────────────
  const [editingFieldIdx,  setEditingFieldIdx]  = useState(null);
  const [editingLabel,     setEditingLabel]     = useState('');
  const [dragIdx,          setDragIdx]          = useState(null);
  const [addFieldKey,      setAddFieldKey]      = useState('');
  const [addMode,          setAddMode]          = useState('preset'); // 'preset' | 'custom'
  const [customFieldKey,   setCustomFieldKey]   = useState('');
  const [customFieldLabel, setCustomFieldLabel] = useState('');

  // ── New-variable creator (Variables Toolbar) ─────────────────────────
  const [newVarKey,   setNewVarKey]   = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');

  // ── File uploads ──────────────────────────────────────────────────────

  const readFile = useCallback((e, key) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => set(key, reader.result);
    reader.readAsDataURL(file);
  }, [set]);

  // ── Certificate type change → load preset ─────────────────────────────

  const changeCertType = useCallback(type => {
    const p  = TYPE_PRESETS[type] || TYPE_PRESETS['tc'];
    const bp = p.border;
    const defaultKeys = TYPE_DEFAULT_FIELDS[type] || DEFAULT_TC_FIELDS;

    const usesLineEditor = type === 'bonafide' || type === 'study';
    const bfDefaults = usesLineEditor ? {
      bonafideLines:          getDefaultLinesForType(type),
      bonafideConductEnabled: true,  // conduct enabled for both bonafide and study
      bonafideConductText:    'His / Her Character & Conduct found to be good.',
      bonafideConductSize:    15,
      bonafideFooterLines:    [],
    } : {};

    const bodyContent = usesLineEditor
      ? generateBonafideBodyHTML({ ...bfDefaults, certType: type })
      : p.bodyContent;

    setTemplateSaved(false);
    setCfg(c => ({
      ...c,
      certType:            type,
      certTitle:           p.certTitle,
      certTitleColor:      p.certTitleColor,
      bodyContent,
      signatures:          p.signatures.map(s => ({ ...s })),
      fields:              buildFieldsFromKeys(defaultKeys),
      borderPreset:        bp.id,
      borderStyle:         bp.style,
      borderThickness:     bp.thickness,
      borderColor:         bp.color,
      borderRadius:        bp.radius,
      innerBorder:         !!bp.inner,
      innerBorderColor:    bp.innerColor    || bp.color,
      innerBorderThickness:bp.innerThickness || 1.5,
      innerBorderInset:    bp.innerInset     || 8,
      ...(type === 'recordsheet' ? { medium_label: 'ENGLISH MEDIUM', edu_type: 'CO-EDUCATION' } : {}),
      ...bfDefaults,
    }));
  }, []);

  // ── Auto-select first cert type for new templates ───────────────────────
  // Any number of templates may be created per type, so the picker never locks.
  // Just make sure a new template starts on a valid backend type unless the user
  // has already chosen one manually.
  const userPickedType = useRef(false);
  React.useEffect(() => {
    if (initialTemplate || userPickedType.current || certificateTypes.length === 0) return;
    const backendVals = certificateTypes.map(t => normalizeCertType(t.certificate_type));
    // Keep the current pick if it's already a valid backend type.
    if (cfg.certType && backendVals.includes(cfg.certType)) return;
    if (backendVals[0] && backendVals[0] !== cfg.certType) changeCertType(backendVals[0]);
  }, [certificateTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-select branch when only one is available ─────────────────────
  const autoBranchSet = useRef(false);
  React.useEffect(() => {
    if (autoBranchSet.current || branches.length !== 1) return;
    autoBranchSet.current = true;
    setCfg(c => c.branchId ? c : { ...c, branchId: String(branches[0].id) });
  }, [branches]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scale A4 preview to fit the scroll container width ──────────
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const recalc = () => {
      const available = el.clientWidth - 48; // subtract 24px padding on each side
      if (available <= 0) return;
      const scale = Math.min(Math.max(parseFloat((available / 794).toFixed(3)), 0.15), 1);
      setA4Scale(scale);
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Border preset change ──────────────────────────────────────────────

  const applyBorderPreset = useCallback(id => {
    const bp = BORDER_PRESETS.find(b => b.id === id);
    if (!bp) return;
    setCfg(c => ({
      ...c,
      borderPreset:        bp.id,
      borderStyle:         bp.style,
      borderThickness:     bp.thickness,
      borderColor:         bp.color,
      borderRadius:        bp.radius,
      innerBorder:         !!bp.inner,
      innerBorderColor:    bp.innerColor    || bp.color,
      innerBorderThickness:bp.innerThickness || 1.5,
      innerBorderInset:    bp.innerInset     || 8,
    }));
  }, []);

  // ── Load from template library ────────────────────────────────────────

  const loadTemplate = useCallback(tmpl => {
    setTemplateSaved(false);
    const parsedCfg = tmpl.config ? { ...tmpl.config } : (() => {
      try { return JSON.parse(tmpl.template_json); } catch { return {}; }
    })();
    const bp = parsedCfg.border || BORDER_PRESETS[0];
    // Ensure fields exist — ID cards use a dynamic renderer (empty body) so always use defaults
    if (!parsedCfg.fields || parsedCfg.fields.length === 0) {
      const certType = parsedCfg.certType || 'tc';
      if (certType === 'idcard') {
        parsedCfg.fields = buildFieldsFromKeys(DEFAULT_IDCARD_FIELDS);
      } else {
        const keys = extractVariablesFromHtml(parsedCfg.bodyContent || '');
        parsedCfg.fields = buildFieldsFromKeys(
          keys.length > 0 ? keys : (TYPE_DEFAULT_FIELDS[certType] || DEFAULT_TC_FIELDS)
        );
      }
    }
    setCfg(c => ({
      ...c,
      ...parsedCfg,
      // Preserve user-entered school details — template library resets these to empty strings
      templateName:        tmpl.name || tmpl.template_name || c.templateName || '',
      branchId:            c.branchId,
      schoolName:          c.schoolName       || parsedCfg.schoolName      || '',
      address:             c.address          || parsedCfg.address          || '',
      phone:               c.phone            || parsedCfg.phone            || '',
      logoUrl:             c.logoUrl          ?? parsedCfg.logoUrl          ?? null,
      recognitionText:     c.recognitionText  || parsedCfg.recognitionText  || DEFAULT_CFG.recognitionText,
      borderPreset:        bp.id,
      borderStyle:         bp.style,
      borderThickness:     bp.thickness,
      borderColor:         bp.color,
      borderRadius:        bp.radius,
      innerBorder:         !!bp.inner,
      innerBorderColor:    bp.innerColor    || bp.color,
      innerBorderThickness:bp.innerThickness || 1.5,
      innerBorderInset:    bp.innerInset     || 8,
    }));
  }, []);

  // ── Insert variable at textarea cursor ────────────────────────────────

  const insertVariable = useCallback(varKey => {
    const ta  = bodyTARef.current;
    const tok = `{{${varKey}}}`;
    if (!ta) { set('bodyContent', cfg.bodyContent + tok); return; }
    const s   = ta.selectionStart;
    const e   = ta.selectionEnd;
    const nv  = cfg.bodyContent.slice(0, s) + tok + cfg.bodyContent.slice(e);
    set('bodyContent', nv);
    setTimeout(() => {
      ta.selectionStart = ta.selectionEnd = s + tok.length;
      ta.focus();
    }, 0);
  }, [cfg.bodyContent, set]);

  // ── Quick HTML snippets ───────────────────────────────────────────────

  const insertSnippet = useCallback(snippet => {
    const ta  = bodyTARef.current;
    if (!ta) { set('bodyContent', cfg.bodyContent + '\n' + snippet); return; }
    const s   = ta.selectionStart;
    const nv  = cfg.bodyContent.slice(0, s) + snippet + cfg.bodyContent.slice(s);
    set('bodyContent', nv);
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = s + snippet.length; ta.focus(); }, 0);
  }, [cfg.bodyContent, set]);

  // ── Custom variables (created from the Variables Toolbar) ─────────────
  // Stored on cfg.customVariables so they persist with the saved template and
  // render as insertable chips alongside the built-in VARIABLES.
  const customVariables = cfg.customVariables || [];

  const addNewVariable = () => {
    const key = newVarKey.trim().replace(/\s+/g, '_').toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (!key) { setSaveMsg('Enter a variable key (e.g. roll_no).'); setTimeout(() => setSaveMsg(''), 2500); return; }
    const exists = VARIABLES.some(v => v.key === key) || customVariables.some(v => v.key === key);
    if (exists) { setSaveMsg('A variable with that key already exists.'); setTimeout(() => setSaveMsg(''), 2500); return; }
    const label = newVarLabel.trim() || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    set('customVariables', [...customVariables, { key, label }]);
    setNewVarKey('');
    setNewVarLabel('');
    insertVariable(key); // drop the token in at the cursor right away
  };

  const removeCustomVariable = (key) =>
    set('customVariables', customVariables.filter(v => v.key !== key));

  // ── Signatures ───────────────────────────────────────────────────────

  const addSig    = ()         => setCfg(c => ({ ...c, signatures: [...c.signatures, { label: 'Signature', align: 'left' }] }));
  const removeSig = idx        => setCfg(c => ({ ...c, signatures: c.signatures.filter((_, i) => i !== idx) }));
  const updateSig = (idx, val) => setCfg(c => ({
    ...c, signatures: c.signatures.map((s, i) => i === idx ? { ...s, label: val } : s),
  }));
  const updateSigAlign = (idx, align) => setCfg(c => ({
    ...c, signatures: c.signatures.map((s, i) => i === idx ? { ...s, align } : s),
  }));

  // ── Dynamic Field Management ──────────────────────────────────────────

  const fields = cfg.fields || [];

  const setFields = (newFields) => setCfg(c => ({ ...c, fields: newFields }));

  const toggleFieldVisibility = (idx) => {
    setFields(fields.map((f, i) => i === idx ? { ...f, visible: !f.visible } : f));
  };

  const startEditField = (idx) => {
    setEditingFieldIdx(idx);
    setEditingLabel(fields[idx].label);
  };

  const saveEditField = () => {
    const key      = fields[editingFieldIdx].key;
    const oldLabel = fields[editingFieldIdx].label;
    const newLabel = editingLabel.trim() || oldLabel;
    setFields(fields.map((f, i) =>
      i === editingFieldIdx ? { ...f, label: newLabel } : f
    ));
    // Rewrite the label cell of the row that holds {{key}} so the preview shows
    // the same name as the field manager (the preset's descriptive label may
    // differ from the field label, so a text match on oldLabel isn't reliable).
    const newBody = setRowLabelByKey(cfg.bodyContent, key, newLabel);
    set('bodyContent', newBody);
    setEditingFieldIdx(null);
    setEditingLabel('');
  };

  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Replace the visible label cell of the <tr> that contains {{key}} with
  // `newLabel`. Picks the first <td> that is neither the value cell (it holds a
  // {{…}} token) nor a standalone serial-number cell, preserving an inline
  // "N.&nbsp;" serial prefix (TC style) when present.
  const setRowLabelByKey = (body, key, newLabel) => {
    const token    = `{{${key}}}`;
    const tokenPos = body.indexOf(token);
    if (tokenPos === -1) return body;
    const trStart = body.lastIndexOf('<tr', tokenPos);
    const trEnd   = body.indexOf('</tr>', tokenPos);
    if (trStart === -1 || trEnd === -1) return body;

    const row = body.slice(trStart, trEnd + 5);
    let done = false;
    const newRow = row.replace(/(<td[^>]*>)([\s\S]*?)(<\/td>)/g, (m, open, inner, close) => {
      if (done) return m;
      if (/\{\{/.test(inner)) return m;                       // value cell — skip
      if (/^\s*\d+\.?(?:&nbsp;|\s)*$/.test(inner)) return m;   // standalone serial cell — skip
      done = true;
      const serial = inner.match(/^\s*\d+\.&nbsp;/);          // keep inline "N.&nbsp;" prefix
      return open + (serial ? serial[0] : '') + newLabel + close;
    });
    return body.slice(0, trStart) + newRow + body.slice(trEnd + 5);
  };

  // Renumber serial labels in body HTML sequentially.
  // Handles both TC-style ("N.&nbsp;" in label td) and Record-style (standalone "N." td).
  const renumberBodyRows = (body) => {
    let tcCounter = 0;
    let recCounter = 0;
    let result = body.replace(/\d+\.&nbsp;/g, () => `${++tcCounter}.&nbsp;`);
    result = result.replace(/(<td[^>]*>)\d+\.(<\/td>)/g, (m, open, close) => `${open}${++recCounter}.${close}`);
    return result;
  };

  // Reorder the table's data <tr> rows in the body HTML so they follow the
  // given field order. Used after a drag-reorder so the live preview reflects
  // the new order (TC / record sheet keep their layout in cfg.bodyContent).
  // A row may carry more than one variable (e.g. Religion / Nationality) — it is
  // placed at the first field key it contains; rows matching no field keep their
  // relative position at the end.
  const reorderBodyRowsToFields = (body, orderedFields) => {
    if (!body || !body.includes('<tr')) return body;

    // Collect every <tr>…</tr> data block (must contain a <td)
    const rows = [];
    let pos = 0;
    while (pos < body.length) {
      const start = body.indexOf('<tr', pos);
      if (start === -1) break;
      const end = body.indexOf('</tr>', start);
      if (end === -1) break;
      const html = body.slice(start, end + 5);
      if (/<td/i.test(html)) rows.push({ start, end: end + 5, html });
      pos = end + 5;
    }
    if (rows.length < 2) return body;

    const rowKeys = rows.map(r =>
      [...r.html.matchAll(/\{\{([a-zA-Z_]+)\}\}/g)].map(m => m[1])
    );
    const used = new Array(rows.length).fill(false);
    const ordered = [];

    orderedFields.forEach(f => {
      const ri = rowKeys.findIndex((keys, i) => !used[i] && keys.includes(f.key));
      if (ri !== -1) { used[ri] = true; ordered.push(rows[ri].html); }
    });
    // Keep any unmatched rows in their original order at the end
    rows.forEach((r, i) => { if (!used[i]) ordered.push(r.html); });

    const first = rows[0].start;
    const last  = rows[rows.length - 1].end;
    return body.slice(0, first) + ordered.join('\n') + body.slice(last);
  };

  // Insert a new <tr> row so that exactly `afterDataRows` data rows precede it.
  // Serial number is a placeholder (0) — call renumberBodyRows afterwards.
  // certType 'recordsheet' uses a 3-column format (serial | label | value).
  const insertRowAtDataIndex = (body, key, label, afterDataRows, certType = 'tc') => {
    const newRow = certType === 'recordsheet'
      ? `\n<tr>` +
        `<td style="padding:5px 8px;width:5%;border:1px solid #ccc;">0.</td>` +
        `<td style="padding:5px 8px;width:52%;border:1px solid #ccc;">${label}</td>` +
        `<td style="padding:5px 8px;border:1px solid #ccc;"><b>{{${key}}}</b></td>` +
        `</tr>`
      : `\n<tr>` +
        `<td style="padding:6px 8px;width:55%;border:1px solid #ccc;">0.&nbsp;${label}</td>` +
        `<td style="padding:6px 8px;border:1px solid #ccc;"><b>{{${key}}}</b></td>` +
        `</tr>`;

    if (!body.includes('<table')) return body + ` {{${key}}}`;

    let dataRowsSeen = 0;
    let pos = 0;
    while (pos < body.length) {
      const trStart = body.indexOf('<tr', pos);
      if (trStart === -1) break;
      const trEnd = body.indexOf('</tr>', trStart);
      if (trEnd === -1) break;
      const rowContent = body.slice(trStart, trEnd + 6);
      if (/<td/i.test(rowContent)) {
        if (dataRowsSeen === afterDataRows) {
          // Insert before this row
          return body.slice(0, trStart) + newRow + '\n' + body.slice(trStart);
        }
        dataRowsSeen++;
      }
      pos = trEnd + 6;
    }
    // afterDataRows >= all existing data rows → append before closing tag
    const closingTag = body.includes('</tbody>') ? '</tbody>' : '</table>';
    const lastIdx = body.lastIndexOf(closingTag);
    if (lastIdx !== -1) return body.slice(0, lastIdx) + newRow + '\n' + body.slice(lastIdx);
    return body + ` {{${key}}}`;
  };

  const deleteField = (idx) => {
    const key = fields[idx].key;
    const newFields = fields.filter((_, i) => i !== idx);
    if (cfg.certType === 'idcard') {
      deletedIdcardPositions.current[key] = idx;
      setCfg(c => ({ ...c, fields: newFields }));
      return;
    }
    const token = `{{${key}}}`;
    let newBody = cfg.bodyContent;

    const tokenPos = newBody.indexOf(token);
    if (tokenPos !== -1) {
      const trStart = newBody.lastIndexOf('<tr', tokenPos);
      const trEnd   = newBody.indexOf('</tr>', tokenPos);
      if (trStart !== -1 && trEnd !== -1) {
        newBody = newBody.slice(0, trStart) + newBody.slice(trEnd + '</tr>'.length);
      }
    }
    newBody = newBody.replace(new RegExp(`\\{\\{${escapeRegex(key)}\\}\\}`, 'g'), '');
    newBody = renumberBodyRows(newBody);
    setCfg(c => ({ ...c, fields: newFields, bodyContent: newBody }));
  };

  // HTML5 drag-and-drop reorder.
  // setData + effectAllowed are required for the drag to actually start — without
  // them Firefox ignores the gesture entirely and Chrome is flaky when the
  // draggable row contains buttons/inputs (as these field rows do).
  const onDragStart = (e, idx) => {
    setDragIdx(idx);
    if (e && e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) { /* ignore */ }
    }
  };
  const onDragOver  = (e)   => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; };
  const onDragEnd   = ()    => setDragIdx(null);
  const onDrop      = (e, idx) => {
    if (e) e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const arr = [...fields];
    const [removed] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, removed);

    // ID card rebuilds its body from cfg.fields each render, and bonafide/study
    // use the line editor — for those, reordering cfg.fields is enough. The
    // table-based bodies (TC / record sheet) hold their row order inside
    // cfg.bodyContent, so the rows must be physically reordered + renumbered to
    // reflect the new order in the preview.
    if (cfg.certType === 'idcard' || cfg.certType === 'bonafide' || cfg.certType === 'study') {
      setFields(arr);
    } else {
      let newBody = reorderBodyRowsToFields(cfg.bodyContent, arr);
      newBody = renumberBodyRows(newBody);
      setCfg(c => ({ ...c, fields: arr, bodyContent: newBody }));
    }
    setTemplateSaved(false);
    setDragIdx(null);
  };

  // Add predefined field — inserts at canonical position for this cert type
  const addField = () => {
    if (!addFieldKey) return;
    if (fields.length >= MAX_FIELDS) {
      setSaveMsg(`Maximum ${MAX_FIELDS} fields allowed in a template.`);
      setTimeout(() => setSaveMsg(''), 3000);
      return;
    }
    if (fields.some(f => f.key === addFieldKey)) {
      setSaveMsg('Field already exists in the template.');
      setTimeout(() => setSaveMsg(''), 2500);
      return;
    }
    const v = VARIABLES.find(v => v.key === addFieldKey);
    const label = v ? v.label : addFieldKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const newField = { key: addFieldKey, label, visible: true, _manual: false };

    if (cfg.certType === 'idcard') {
      let insertAt;
      const remembered = deletedIdcardPositions.current[addFieldKey];
      if (remembered !== undefined) {
        // Restore to the exact slot it was deleted from (clamped to current length)
        insertAt = Math.min(remembered, fields.length);
        delete deletedIdcardPositions.current[addFieldKey];
      } else {
        // New field — use canonical order as default placement
        const canonicalKeys = TYPE_DEFAULT_FIELDS.idcard || DEFAULT_IDCARD_FIELDS;
        const newKeyCanonIdx = canonicalKeys.indexOf(addFieldKey);
        insertAt = newKeyCanonIdx === -1
          ? fields.length
          : fields.filter(f => {
              const ci = canonicalKeys.indexOf(f.key);
              return ci !== -1 && ci < newKeyCanonIdx;
            }).length;
      }
      const newFields = [...fields];
      newFields.splice(insertAt, 0, newField);
      setCfg(c => ({ ...c, fields: newFields }));
      setAddFieldKey('');
      return;
    }

    // Determine canonical order for the current cert type
    const canonicalKeys = TYPE_DEFAULT_FIELDS[cfg.certType] || DEFAULT_TC_FIELDS;
    const newKeyCanonIdx = canonicalKeys.indexOf(addFieldKey);

    // Count how many current fields have a lower canonical index (go before this one)
    const insertAfterCount = newKeyCanonIdx === -1
      ? fields.length  // not in canonical list → append
      : fields.filter(f => {
          const ci = canonicalKeys.indexOf(f.key);
          return ci !== -1 && ci < newKeyCanonIdx;
        }).length;

    const newFields = [...fields];
    newFields.splice(insertAfterCount, 0, newField);

    let newBody = insertRowAtDataIndex(cfg.bodyContent, addFieldKey, label, insertAfterCount, cfg.certType);
    newBody = renumberBodyRows(newBody);

    setCfg(c => ({ ...c, fields: newFields, bodyContent: newBody }));
    setAddFieldKey('');
  };

  // Add a custom field — always appends at the end
  const addCustomField = () => {
    const key = customFieldKey.trim().replace(/\s+/g, '_').toLowerCase();
    if (!key) return;
    if (fields.length >= MAX_FIELDS) {
      setSaveMsg(`Maximum ${MAX_FIELDS} fields allowed in a template.`);
      setTimeout(() => setSaveMsg(''), 3000);
      return;
    }
    if (fields.some(f => f.key === key)) {
      setSaveMsg('Field already exists in the template.');
      setTimeout(() => setSaveMsg(''), 2500);
      return;
    }
    const label = customFieldLabel.trim() || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const newField = { key, label, visible: true, _manual: true };

    if (cfg.certType === 'idcard') {
      setCfg(c => ({ ...c, fields: [...fields, newField] }));
      setCustomFieldKey('');
      setCustomFieldLabel('');
      return;
    }

    let newBody = insertRowAtDataIndex(cfg.bodyContent, key, label, fields.length, cfg.certType);
    newBody = renumberBodyRows(newBody);

    setCfg(c => ({ ...c, fields: [...fields, newField], bodyContent: newBody }));
    setCustomFieldKey('');
    setCustomFieldLabel('');
  };

  // ── Preview HTML ─────────────────────────────────────────────────────

  const previewHTML = useMemo(() => {
    // Table-based certs (TC / record sheet / conduct) now render their body
    // dynamically from cfg.fields inside buildCertHTML, so passing empty data
    // yields the "field name : -" skeleton. Bonafide/study/id card keep their
    // normal sample-data preview.
    const isTableType = cfg.certType !== 'bonafide' && cfg.certType !== 'study' && cfg.certType !== 'idcard';
    return buildCertHTML(cfg, isTableType ? {} : SAMPLE_DATA);
  }, [cfg]);

  // ── Save template ─────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!cfg.templateName.trim() || !cfg.branchId || !cfg.certType) {
      setShowErrors(true);
      const missing = [
        !cfg.certType        && 'Certificate Type',
        !cfg.templateName.trim() && 'Template Name',
        !cfg.branchId        && 'Branch',
      ].filter(Boolean).join(', ');
      setSaveMsg(`Please select: ${missing}.`);
      setTimeout(() => setSaveMsg(''), 4000);
      return;
    }
    setShowErrors(false);

    const isEditing = !!(initialTemplate?.id && updateTemplate);

    setSaving(true);
    try {
      if (isEditing) {
        // ── Update existing template ──────────────────────────────────
        await updateTemplate({
          id:           initialTemplate.id,
          templateName: cfg.templateName,
          branchId:     cfg.branchId,
          templateJson: JSON.stringify(cfg),
        });
      } else {
        // ── Create new template — needs certificateTypeId ─────────────
        const matched = certificateTypes.find(
          t => normalizeCertType(t.certificate_type) === cfg.certType
        );
        const certificateTypeId = matched?.id;

        if (!certificateTypeId) {
          setSaveMsg('Certificate type not found. Please re-select the type.');
          setTimeout(() => setSaveMsg(''), 4000);
          setSaving(false);
          return;
        }

        await saveTemplate({
          templateName:     cfg.templateName,
          certType:         cfg.certType,
          branchId:         cfg.branchId,
          templateJson:     JSON.stringify(cfg),
          certificateTypeId,
        });
      }

      // Mirror to localStorage
      const localId = initialTemplate?.id || Date.now();
      saveTemplateToStorage({
        id:            localId,
        template_name: cfg.templateName,
        template_type: cfg.certType.toUpperCase(),
        template_html: buildCertHTML(cfg, {}),
        template_css:  buildCertCSS(cfg),
        template_json: JSON.stringify(cfg),
        created_at:    new Date().toISOString(),
      });

      setSaveMsg(isEditing ? 'Template updated successfully!' : 'Template saved successfully!');
      setTemplateSaved(true);
      if (onTemplateSaved) onTemplateSaved();
    } catch (err) {
      setSaveMsg(isEditing ? 'Update failed. Please try again.' : 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
    setTimeout(() => setSaveMsg(''), 3000);
  };

  // ── Print ─────────────────────────────────────────────────────────────

  const handlePrint = () => {
    // ID Card: print each side on its own portrait 54mm × 86mm page (one card
    // per page, filling it edge-to-edge), matching the reference card PDF.
    // Read from the live preview (rendered with SAMPLE_DATA) so the front shows
    // real data — buildCertHTML with empty data collapses the front into a back.
    if (cfg.certType === 'idcard') {
      const root  = previewRef.current;
      const front = root && root.querySelector('#dcb-idcard-front');
      const back  = root && root.querySelector('#dcb-idcard-back');
      const cards = [front, back]
        .filter(Boolean)
        .map(el => el.innerHTML
          // Full-bleed on the card-sized page: drop rounded corners + shadow.
          .replace(/border-radius\s*:[^;"']+;?/gi, '')
          .replace(/box-shadow\s*:[^;"']+;?/gi, ''));
      const body = (cards.length ? cards : [buildCertHTML(cfg, SAMPLE_DATA)])
        .map(c => `<div class="idpage">${c}</div>`)
        .join('');
      const win = window.open('', '_blank');
      win.document.write(
        `<!DOCTYPE html><html><head><title>${cfg.templateName}</title>` +
        `<base href="${window.location.origin}/">` +
        `<style>` +
        `@page{size:54mm 86mm;margin:0;}` +
        `*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact;}` +
        `html,body{margin:0;padding:0;}` +
        `.idpage{width:54mm;height:86mm;overflow:hidden;page-break-after:always;}` +
        `.idpage:last-child{page-break-after:auto;}` +
        // Force the card to fill the page exactly (the green card is already
        // 54×86mm; this also normalises the corners for full-bleed printing).
        `.idpage>div{width:54mm !important;height:86mm !important;border-radius:0 !important;}` +
        `</style></head><body>${body}</body></html>`
      );
      win.document.close();
      setTimeout(() => { win.print(); win.close(); }, 400);
      return;
    }

    let html;
    if (cfg.certType === 'bonafide') {
      // Replace every token with a non-breaking space so the parent span's
      // border-bottom shows as a clean blank underline (avoids nested double-underline spans)
      const emptyData = Object.fromEntries(VARIABLES.map(v => [v.key, '          ']));
      html = buildCertHTML(cfg, emptyData);
      // Remove inner border div — keep only the outer frame
      html = html.replace(/<div\s+style="[^"]*pointer-events:none[^"]*"><\/div>/gi, '');
    } else {
      html = buildCertHTML(cfg, {});
      // TC, record sheet, ID card: strip underline styles
      html = html.replace(/\bborder-bottom\s*:[^;'"]+;?\s*/gi, '');
      html = html.replace(/\btext-decoration\s*:\s*underline[^;'"]*;?\s*/gi, '');
    }
    const win = window.open('', '_blank');
    win.document.write(
      `<!DOCTYPE html><html><head><title>${cfg.templateName}</title>` +
      `<style>@media print{body{margin:0;}}body{margin:20px;}</style></head>` +
      `<body>${html}</body></html>`
    );
    win.document.close();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  // ── Download PDF ──────────────────────────────────────────────────────

  const handlePDF = async () => {
    if (!previewRef.current) return;
    const spans = Array.from(previewRef.current.querySelectorAll('span'));
    const saved = spans.map(s => s.style.borderBottom);
    spans.forEach(s => { s.style.borderBottom = 'none'; });
    // Inline the logo/watermark as base64 so they render in the captured PDF.
    const restoreImages = await inlineImagesForCapture(previewRef.current);
    try {
      if (cfg.certType === 'idcard') {
        // ID cards are small relative to A4 — render front/back at their own
        // aspect ratio and a fixed real-world width instead of stretching the
        // whole (mostly empty) preview across the page, which made the
        // layout look like a thin horizontal strip with a tiny photo.
        const front = previewRef.current.querySelector('#dcb-idcard-front');
        const back  = previewRef.current.querySelector('#dcb-idcard-back');
        const pdf   = new jsPDF('p', 'mm', 'a4');
        const pageW = pdf.internal.pageSize.getWidth();
        const cardW = back ? 80 : 100; // mm
        let y = 15;
        for (const el of [front, back]) {
          if (!el) continue;
          const canvas  = await html2canvas(el, { scale: 3, useCORS: true });
          const imgData = canvas.toDataURL('image/png');
          const cardH   = (canvas.height / canvas.width) * cardW;
          const x       = (pageW - cardW) / 2;
          pdf.addImage(imgData, 'PNG', x, y, cardW, cardH);
          y += cardH + 10;
        }
        pdf.save(`${cfg.templateName || 'certificate'}.pdf`);
        return;
      }

      const canvas  = await html2canvas(previewRef.current, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf     = new jsPDF('p', 'mm', 'a4');
      const pageW   = pdf.internal.pageSize.getWidth();
      const pageH   = pdf.internal.pageSize.getHeight();
      // Fit the whole certificate onto ONE A4 page within a uniform margin, then
      // top-align it (block sits at the top, equal space on left & right) instead
      // of centering vertically — which otherwise leaves a gap above the border.
      const M = 6; // mm margin on all sides
      const availW = pageW - M * 2;
      const availH = pageH - M * 2;
      const ratio = canvas.width / canvas.height;
      let renderW = availW;
      let renderH = availW / ratio;
      if (renderH > availH) {
        renderH = availH;
        renderW = availH * ratio;
      }
      const x = (pageW - renderW) / 2; // equal left/right margins
      const y = (pageH - renderH) / 2; // equal top/bottom margins
      pdf.addImage(imgData, 'PNG', x, y, renderW, renderH);
      pdf.save(`${cfg.templateName || 'certificate'}.pdf`);
    } catch (err) {
      console.error('PDF error:', err);
    } finally {
      restoreImages();
      spans.forEach((s, i) => { s.style.borderBottom = saved[i]; });
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="dcb-root">

      {/* TOP BAR */}
      <div className="dcb-topbar">
        <div className="dcb-topbar-left">
          <span className="dcb-topbar-icon">🏗</span>
          <span className="dcb-topbar-title">Certificate Template Builder</span>
        </div>
        <div className="dcb-topbar-right">
          <input
            className="dcb-name-input"
            value={cfg.templateName}
            onChange={e => set('templateName', e.target.value)}
            placeholder="Template name *"
            style={showErrors && !cfg.templateName.trim() ? { border: '2px solid #e53935' } : undefined}
          />
          <select
            className="dcb-name-input"
            style={{ minWidth: 160, ...(showErrors && !cfg.branchId ? { border: '2px solid #e53935' } : {}) }}
            value={cfg.branchId}
            onChange={e => set('branchId', e.target.value)}
          >
            <option value="">-- Select Branch * --</option>
            {branches.map(b => (
              <option key={b.id} value={b.id}>{b.branch_name}</option>
            ))}
          </select>
          <button
            className={`dcb-tbtn ${templateSaved ? 'dcb-tbtn--saved' : 'dcb-tbtn--save'}`}
            onClick={handleSave}
            disabled={templateSaved || saving}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm" style={{ marginRight: 6 }}></span>{initialTemplate?.id ? 'Updating...' : 'Saving...'}</>
              : templateSaved ? '✓ Saved'
              : initialTemplate?.id ? '✏ Update' : '💾 Save'}
          </button>
          {templateSaved && (
            <button className="dcb-tbtn dcb-tbtn--print" onClick={handlePrint}>🖨 Print</button>
          )}
          {templateSaved && (
            <button className="dcb-tbtn dcb-tbtn--pdf" onClick={handlePDF}>📥 PDF</button>
          )}
        </div>
      </div>

      {saveMsg && <div className={`dcb-toast${showErrors ? ' dcb-toast--error' : ''}`}>{saveMsg}</div>}

      {/* MAIN BODY */}
      <div className="dcb-body">

        {/* ══ LEFT PANEL ══ */}
        <div className="dcb-left">

          {/* Certificate Type */}
          <Section title="Certificate Type *" open={sections.type} onToggle={() => toggle('type')}>
            <select
              className="dcb-select"
              value={cfg.certType}
              onChange={e => { userPickedType.current = true; changeCertType(e.target.value); }}
              style={showErrors && !cfg.certType ? { border: '2px solid #e53935' } : undefined}
            >
              {(certificateTypes.length > 0
                ? certificateTypes.map(t => ({ value: normalizeCertType(t.certificate_type), label: t.certificate_type }))
                : CERT_TYPES
              ).map(t => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Section>

          {/* School Header */}
          <Section title="School Header" open={sections.header} onToggle={() => toggle('header')}>

            <div className="dcb-logo-area">
              {cfg.logoUrl ? (
                <div className="dcb-logo-preview-wrap">
                  <img src={cfg.logoUrl} alt="logo" className="dcb-logo-img" />
                  <button className="dcb-logo-remove" onClick={() => set('logoUrl', null)}>×</button>
                </div>
              ) : (
                <label className="dcb-upload-zone">
                  <span className="dcb-upload-icon">📤</span>
                  <span>Upload School Logo</span>
                  <input type="file" accept="image/*" onChange={e => readFile(e, 'logoUrl')} hidden />
                </label>
              )}
            </div>

            <FRow label="School Name">
              <input className="dcb-input" value={cfg.schoolName}
                placeholder="Enter school name"
                onChange={e => set('schoolName', e.target.value)} />
            </FRow>
            <FRow label="Address">
              <input className="dcb-input" value={cfg.address}
                placeholder="Enter school address"
                onChange={e => set('address', e.target.value)} />
            </FRow>
            {cfg.certType !== 'idcard' && (
              <>
                <FRow label="Phone">
                  <div style={{ display: 'flex', alignItems: 'stretch' }}>
                    <span style={{
                      background: '#f0f2f5', border: '1px solid #ccc', borderRight: 'none',
                      borderRadius: '4px 0 0 4px', padding: '0 8px',
                      display: 'flex', alignItems: 'center',
                      fontSize: 13, color: '#666', whiteSpace: 'nowrap',
                    }}>+91</span>
                    <input
                      className="dcb-input"
                      style={{ borderRadius: '0 4px 4px 0', flex: 1, minWidth: 0 }}
                      value={(cfg.phone || '').replace(/^\+91\s*/, '')}
                      placeholder="7569761658, 8522943573"
                      maxLength={30}
                      onChange={e => {
                        const cleaned = e.target.value.replace(/[^\d,\s]/g, '').slice(0, 30);
                        set('phone', cleaned ? '+91 ' + cleaned : '');
                      }}
                    />
                  </div>
                  <p style={{ fontSize: 11, color: '#888', margin: '3px 0 0' }}>
                    Two numbers comma-separated, max 30 chars
                  </p>
                </FRow>
                <FRow label="Recognition Text">
                  <input className="dcb-input" value={cfg.recognitionText}
                    onChange={e => set('recognitionText', e.target.value)} />
                </FRow>
                {cfg.certType === 'recordsheet' && (<>
                  <FRow label="Medium (left label)">
                    <input className="dcb-input" value={cfg.medium_label || ''}
                      placeholder="e.g. ENGLISH MEDIUM"
                      onChange={e => set('medium_label', e.target.value)} />
                  </FRow>
                  <FRow label="Education Type (right label)">
                    <input className="dcb-input" value={cfg.edu_type || ''}
                      placeholder="e.g. CO-EDUCATION"
                      onChange={e => set('edu_type', e.target.value)} />
                  </FRow>
                  <FRow label="UDISE No.">
                    <input className="dcb-input" value={cfg.schoolCode || ''}
                      placeholder="e.g. 36221191912"
                      onChange={e => set('schoolCode', e.target.value)} />
                  </FRow>
                </>)}
                <FRow label="Certificate Title">
                  <input className="dcb-input" value={cfg.certTitle}
                    onChange={e => set('certTitle', e.target.value)} />
                </FRow>
                <FRow label="Alignment">
                  <div className="dcb-align-group">
                    {['left', 'center', 'right'].map(a => (
                      <button key={a}
                        className={`dcb-align-btn${cfg.alignment === a ? ' active' : ''}`}
                        onClick={() => set('alignment', a)}
                      >
                        {a === 'left' ? '◀ Left' : a === 'center' ? '■ Center' : 'Right ▶'}
                      </button>
                    ))}
                  </div>
                </FRow>
              </>
            )}

            <FRow label="Font Family">
              <select
                className="dcb-select"
                value={cfg.fontFamily || "'Times New Roman', Times, serif"}
                onChange={e => set('fontFamily', e.target.value)}
              >
                {FONT_OPTIONS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </FRow>

            {cfg.certType !== 'idcard' && (
              <>
                <div className="dcb-pair">
                  <FRow label="Name Size">
                    <div className="dcb-num-input">
                      <input type="number" min={12} max={40} value={cfg.schoolNameSize}
                        onChange={e => set('schoolNameSize', +e.target.value)} />
                      <span>px</span>
                    </div>
                  </FRow>
                  <FRow label="Name Color">
                    <input type="color" className="dcb-color" value={cfg.schoolNameColor}
                      onChange={e => set('schoolNameColor', e.target.value)} />
                  </FRow>
                </div>
                <div className="dcb-pair">
                  <FRow label="Title Size">
                    <div className="dcb-num-input">
                      <input type="number" min={10} max={30} value={cfg.certTitleSize}
                        onChange={e => set('certTitleSize', +e.target.value)} />
                      <span>px</span>
                    </div>
                  </FRow>
                  <FRow label="Title Color">
                    <input type="color" className="dcb-color" value={cfg.certTitleColor}
                      onChange={e => set('certTitleColor', e.target.value)} />
                  </FRow>
                </div>
              </>
            )}

          </Section>

          {/* ── ID Card Design (only when certType === idcard) ── */}
          {cfg.certType === 'idcard' && (
            <Section title="ID Card Design" open={sections.idcard} onToggle={() => toggle('idcard')}>
              <FRow label="Card Style">
                <select className="dcb-select" value={cfg.idCardStyle || 'wave'} onChange={e => set('idCardStyle', e.target.value)}>
                  <option value="wave">Purple Wave (Classic)</option>
                  <option value="sidebar">Teal Split (Modern)</option>
                  <option value="formal">Red Formal (Academic)</option>
                  <option value="dark">Dark Premium (Elegant)</option>
                  <option value="green">Green Wave (Front &amp; Back)</option>
                </select>
              </FRow>
              <FRow label="Accent Color">
                <input type="color" className="dcb-color"
                  value={cfg.certTitleColor || '#6a0dad'}
                  onChange={e => set('certTitleColor', e.target.value)} />
              </FRow>
              <p className="dcb-hint" style={{ marginTop: 8 }}>Upload a sample photo to preview the card layout.</p>
              {cfg.idCardPhotoUrl ? (
                <div className="dcb-logo-preview-wrap">
                  <img src={cfg.idCardPhotoUrl} alt="preview" className="dcb-logo-img" style={{ borderRadius: '50%' }} />
                  <button className="dcb-logo-remove" onClick={() => set('idCardPhotoUrl', null)}>×</button>
                </div>
              ) : (
                <label className="dcb-upload-zone">
                  <span className="dcb-upload-icon">👤</span>
                  <span>Upload Preview Photo</span>
                  <input type="file" accept="image/*" onChange={e => readFile(e, 'idCardPhotoUrl')} hidden />
                </label>
              )}

              <p className="dcb-hint" style={{ marginTop: 12 }}>Principal signature shown above the “Principal” label on the front of the card.</p>
              {cfg.idCardSignature ? (
                <div className="dcb-logo-preview-wrap">
                  <img src={cfg.idCardSignature} alt="principal signature" className="dcb-logo-img" />
                  <button className="dcb-logo-remove" onClick={() => set('idCardSignature', null)}>×</button>
                </div>
              ) : (
                <label className="dcb-upload-zone">
                  <span className="dcb-upload-icon">✍</span>
                  <span>Upload Principal Signature</span>
                  <input type="file" accept="image/*" onChange={e => readFile(e, 'idCardSignature')} hidden />
                </label>
              )}
            </Section>
          )}

          {/* ── Dynamic Field Manager ──
              Hidden for bonafide/study (they use the Lines Editor) and id card
              (its details are managed in the ID Card Design section). */}
          {cfg.certType !== 'bonafide' && cfg.certType !== 'study' && cfg.certType !== 'idcard' && (
          <Section title={`Dynamic Fields (${fields.filter(f => !getHeaderFieldKeys(cfg.certType).includes(f.key)).length} / ${MAX_FIELDS})`} open={sections.fields} onToggle={() => toggle('fields')}>

            <p className="dcb-hint">
              Drag to reorder · Toggle 👁 to show/hide · Click ✎ to rename · Click × to delete.
            </p>
            <p className="dcb-hint">
              Admission No, TC No &amp; Issue Date appear in the certificate header, so they aren't listed here.
            </p>
            {fields.length >= MAX_FIELDS && (
              <div className="dcb-fields-limit-banner">
                🚫 Maximum {MAX_FIELDS} fields reached. Delete a field to add more.
              </div>
            )}

            <div className="dcb-fields-list">
              {fields.map((field, idx) => (
                getHeaderFieldKeys(cfg.certType).includes(field.key) ? null : (
                <div
                  key={field.key}
                  className={`dcb-field-row${field.visible ? '' : ' dcb-field-row--hidden'}${dragIdx === idx ? ' dcb-field-row--dragging' : ''}`}
                  draggable
                  onDragStart={(e) => onDragStart(e, idx)}
                  onDragOver={onDragOver}
                  onDrop={(e) => onDrop(e, idx)}
                  onDragEnd={onDragEnd}
                >
                  {/* Drag handle */}
                  <span className="dcb-field-drag" title="Drag to reorder">⠿</span>

                  {/* Label / edit mode */}
                  <div className="dcb-field-info">
                    {editingFieldIdx === idx ? (
                      <input
                        className="dcb-field-edit-input"
                        value={editingLabel}
                        onChange={e => setEditingLabel(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveEditField()}
                        autoFocus
                      />
                    ) : (
                      <span className={`dcb-field-label${field.visible ? '' : ' dcb-field-label--dim'}`}>
                        {field.label}
                        {field._manual && <span className="dcb-field-manual-badge" title="Manually added">+</span>}
                      </span>
                    )}
                    <span className="dcb-field-key">{`{{${field.key}}}`}</span>
                  </div>

                  <div className="dcb-field-actions">
                    {editingFieldIdx === idx ? (
                      <button className="dcb-field-save-btn" onClick={saveEditField} title="Save label">✓</button>
                    ) : (
                      <button className="dcb-field-edit-btn" onClick={() => startEditField(idx)} title="Edit label">✎</button>
                    )}

                    {/* Visibility toggle */}
                    <button
                      className={`dcb-field-vis-btn${field.visible ? ' active' : ''}`}
                      onClick={() => toggleFieldVisibility(idx)}
                      title={field.visible ? 'Hide field' : 'Show field'}
                    >
                      {field.visible ? '👁' : '🚫'}
                    </button>

                    {/* Delete field */}
                    <button
                      className="dcb-field-del-btn"
                      onClick={() => deleteField(idx)}
                      title="Delete field permanently"
                    >
                      ×
                    </button>
                  </div>
                </div>
                )
              ))}
            </div>

            {/* Add new field — tabbed: Predefined or Custom */}
            <div className="dcb-add-field-section">
              <div className="dcb-add-mode-tabs">
                <button
                  className={`dcb-mode-tab${addMode === 'preset' ? ' active' : ''}`}
                  onClick={() => setAddMode('preset')}
                >
                  Predefined Field
                </button>
                <button
                  className={`dcb-mode-tab${addMode === 'custom' ? ' active' : ''}`}
                  onClick={() => setAddMode('custom')}
                >
                  Custom Field
                </button>
              </div>

              {addMode === 'preset' ? (
                <div className="dcb-add-field-row">
                  <select
                    className="dcb-select dcb-add-field-select"
                    value={addFieldKey}
                    onChange={e => setAddFieldKey(e.target.value)}
                  >
                    <option value="">-- Select a field --</option>
                    {VARIABLES
                      .filter(v => !fields.some(f => f.key === v.key))
                      .map(v => (
                        <option key={v.key} value={v.key}>{v.label}</option>
                      ))
                    }
                  </select>
                  <button className="dcb-add-field-btn" onClick={addField} disabled={!addFieldKey || fields.length >= MAX_FIELDS}>
                    + Add
                  </button>
                </div>
              ) : (
                <div className="dcb-custom-field-form">
                  <input
                    className="dcb-input"
                    placeholder="Field key (e.g. award_name)"
                    value={customFieldKey}
                    onChange={e => setCustomFieldKey(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCustomField()}
                  />
                  <input
                    className="dcb-input"
                    placeholder="Display label (e.g. Award Name)"
                    value={customFieldLabel}
                    onChange={e => setCustomFieldLabel(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addCustomField()}
                  />
                  <p className="dcb-hint" style={{ marginTop: 4 }}>
                    Key becomes <code>{`{{${customFieldKey.trim().replace(/\s+/g,'_').toLowerCase() || 'field_key'}}}`}</code> in the template body.
                  </p>
                  <button
                    className="dcb-add-field-btn"
                    onClick={addCustomField}
                    disabled={!customFieldKey.trim() || fields.length >= MAX_FIELDS}
                  >
                    + Add Custom Field
                  </button>
                </div>
              )}
            </div>

          </Section>
          )}

          {/* Variables Toolbar — not needed for ID card (no HTML body) */}
          {cfg.certType !== 'idcard' && (
          <Section title="Variables Toolbar" open={sections.variables} onToggle={() => toggle('variables')}>
            <p className="dcb-hint">Click a variable to insert at cursor position in the body editor.</p>
            <div className="dcb-var-grid">
              {VARIABLES.map(v => (
                <button key={v.key} className="dcb-var-chip" onClick={() => insertVariable(v.key)}>
                  {v.label}
                </button>
              ))}
              {customVariables.map(v => (
                <span key={v.key} className="dcb-var-chip dcb-var-chip--custom">
                  <button className="dcb-var-chip-ins" onClick={() => insertVariable(v.key)} title={`Insert {{${v.key}}}`}>
                    {v.label}
                  </button>
                  <button className="dcb-var-chip-rm" onClick={() => removeCustomVariable(v.key)} title="Remove variable">×</button>
                </span>
              ))}
            </div>

            {/* Create a new custom variable */}
            <div className="dcb-newvar">
              <p className="dcb-hint" style={{ marginTop: 12 }}>Create a new variable:</p>
              <div className="dcb-newvar-row">
                <input
                  className="dcb-input"
                  value={newVarKey}
                  onChange={e => setNewVarKey(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addNewVariable(); }}
                  placeholder="key (e.g. roll_no)"
                />
                <input
                  className="dcb-input"
                  value={newVarLabel}
                  onChange={e => setNewVarLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addNewVariable(); }}
                  placeholder="Label (e.g. Roll No)"
                />
                <button className="dcb-add-field-btn" onClick={addNewVariable} disabled={!newVarKey.trim()}>
                  + Add
                </button>
              </div>
              <p className="dcb-hint">
                Inserts <code>{'{{key}}'}</code> in the body. The value renders only if the student data supplies that key.
              </p>
            </div>

            <p className="dcb-hint" style={{ marginTop: 12 }}>Quick HTML snippets:</p>
            <div className="dcb-snippet-row">
              <button className="dcb-snippet"
                onClick={() => insertSnippet(
                  `\n<tr><td style="padding:6px 8px;border:1px solid #ccc;">Field Label</td>` +
                  `<td style="padding:6px 8px;border:1px solid #ccc;"><b>{{variable}}</b></td></tr>`
                )}>+ Table Row</button>
              <button className="dcb-snippet"
                onClick={() => insertSnippet(`\n<p>Enter text with <b>{{student_name}}</b> here.</p>`)}>
                + Paragraph</button>
              <button className="dcb-snippet"
                onClick={() => insertSnippet(`\n<br/>`)}>+ Line Break</button>
              <button className="dcb-snippet"
                onClick={() => insertSnippet(
                  `\n<hr style="border:none;border-top:1px solid #ccc;margin:8px 0;" />`
                )}>+ Divider</button>
            </div>
          </Section>
          )}

          {/* Lines Editor — for bonafide and study cert types */}
          {(cfg.certType === 'bonafide' || cfg.certType === 'study') && (
            <Section
              title={cfg.certType === 'study' ? 'Study Lines Editor' : 'Bonafide Lines Editor'}
              open={sections.bonafide}
              onToggle={() => toggle('bonafide')}
            >
              <BonafideLineEditor
                cfg={cfg}
                onUpdate={(updates) => setCfg(c => ({ ...c, ...updates }))}
              />
            </Section>
          )}

          {/* Body Content Editor — not needed for ID card */}
          {cfg.certType !== 'idcard' && (
          <Section title="Body Content (HTML)" open={sections.body} onToggle={() => toggle('body')}>
            <textarea
              ref={bodyTARef}
              className="dcb-body-editor"
              value={cfg.bodyContent}
              onChange={e => set('bodyContent', e.target.value)}
              rows={14}
              spellCheck={false}
            />
            <div className="dcb-pair" style={{ marginTop: 10 }}>
              <FRow label="Font Size">
                <div className="dcb-num-input">
                  <input type="number" min={10} max={22} value={cfg.bodyFontSize}
                    onChange={e => set('bodyFontSize', +e.target.value)} />
                  <span>px</span>
                </div>
              </FRow>
              <FRow label="Line Height">
                <div className="dcb-num-input">
                  <input type="number" min={1} max={4} step={0.1} value={cfg.bodyLineHeight}
                    onChange={e => set('bodyLineHeight', +e.target.value)} />
                  <span>×</span>
                </div>
              </FRow>
            </div>
          </Section>
          )}

          {/* Border Design — not needed for ID card */}
          {cfg.certType !== 'idcard' && (
          <Section title="Border Design" open={sections.border} onToggle={() => toggle('border')}>
            <p className="dcb-subsection-label">Border Image</p>
            {cfg.borderImageUrl ? (
              <div className="dcb-logo-preview-wrap">
                <img src={cfg.borderImageUrl} alt="border" className="dcb-logo-img" />
                <button className="dcb-logo-remove" onClick={() => set('borderImageUrl', null)}>×</button>
              </div>
            ) : (
              <label className="dcb-upload-zone" style={{ padding: 10 }}>
                <span className="dcb-upload-icon">🖼</span>
                <span>Upload Border Image</span>
                <input type="file" accept="image/*" onChange={e => readFile(e, 'borderImageUrl')} hidden />
              </label>
            )}
            <div className="dcb-divider" />
            <div className="dcb-border-presets">
              {BORDER_PRESETS.map(bp => (
                <button key={bp.id}
                  className={`dcb-bp-btn${cfg.borderPreset === bp.id ? ' active' : ''}`}
                  style={{ '--bp-color': bp.color === 'transparent' ? '#ccc' : bp.color }}
                  onClick={() => applyBorderPreset(bp.id)}
                >
                  {bp.name}
                </button>
              ))}
            </div>
            <div className="dcb-pair">
              <FRow label="Thickness">
                <div className="dcb-num-input">
                  <input type="number" min={0} max={20} value={cfg.borderThickness}
                    onChange={e => set('borderThickness', +e.target.value)} />
                  <span>px</span>
                </div>
              </FRow>
              <FRow label="Color">
                <input type="color" className="dcb-color" value={cfg.borderColor}
                  onChange={e => set('borderColor', e.target.value)} />
              </FRow>
            </div>
            <FRow label="Style">
              <select className="dcb-select" value={cfg.borderStyle}
                onChange={e => set('borderStyle', e.target.value)}>
                <option value="solid">Solid</option>
                <option value="double">Double</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
                <option value="groove">Groove</option>
                <option value="ridge">Ridge</option>
                <option value="none">None</option>
              </select>
            </FRow>
            <FRow label="Radius">
              <div className="dcb-num-input">
                <input type="number" min={0} max={50} value={cfg.borderRadius}
                  onChange={e => set('borderRadius', +e.target.value)} />
                <span>px</span>
              </div>
            </FRow>
            <FRow label="Inner Border">
              <label className="dcb-toggle">
                <input type="checkbox" checked={cfg.innerBorder}
                  onChange={e => set('innerBorder', e.target.checked)} />
                <span className="dcb-toggle-track" />
              </label>
            </FRow>
            {cfg.innerBorder && (
              <>
                <div className="dcb-pair">
                  <FRow label="Inner Thickness">
                    <div className="dcb-num-input">
                      <input type="number" min={0.5} max={5} step={0.5}
                        value={cfg.innerBorderThickness}
                        onChange={e => set('innerBorderThickness', +e.target.value)} />
                      <span>px</span>
                    </div>
                  </FRow>
                  <FRow label="Inner Color">
                    <input type="color" className="dcb-color" value={cfg.innerBorderColor}
                      onChange={e => set('innerBorderColor', e.target.value)} />
                  </FRow>
                </div>
                <FRow label="Inset Distance">
                  <div className="dcb-num-input">
                    <input type="number" min={4} max={30} value={cfg.innerBorderInset}
                      onChange={e => set('innerBorderInset', +e.target.value)} />
                    <span>px</span>
                  </div>
                </FRow>
              </>
            )}
          </Section>
          )}

          {/* Background Design — not needed for ID card */}
          {cfg.certType !== 'idcard' && (
          <Section title="Background Design" open={sections.background} onToggle={() => toggle('background')}>
            <FRow label="Type">
              <select className="dcb-select" value={cfg.bgType}
                onChange={e => set('bgType', e.target.value)}>
                <option value="white">White</option>
                <option value="color">Solid Color</option>
                <option value="gradient">Gradient</option>
                <option value="image">Image Upload</option>
              </select>
            </FRow>
            {cfg.bgType === 'color' && (
              <FRow label="Background Color">
                <input type="color" className="dcb-color" value={cfg.bgColor}
                  onChange={e => set('bgColor', e.target.value)} />
              </FRow>
            )}
            {cfg.bgType === 'gradient' && (
              <>
                <div className="dcb-pair">
                  <FRow label="Start Color">
                    <input type="color" className="dcb-color" value={cfg.bgGradientStart}
                      onChange={e => set('bgGradientStart', e.target.value)} />
                  </FRow>
                  <FRow label="End Color">
                    <input type="color" className="dcb-color" value={cfg.bgGradientEnd}
                      onChange={e => set('bgGradientEnd', e.target.value)} />
                  </FRow>
                </div>
                <FRow label="Direction">
                  <select className="dcb-select" value={cfg.bgGradientDir}
                    onChange={e => set('bgGradientDir', e.target.value)}>
                    <option value="to bottom">Top → Bottom</option>
                    <option value="to right">Left → Right</option>
                    <option value="to bottom right">Diagonal ↘</option>
                  </select>
                </FRow>
              </>
            )}
            {cfg.bgType === 'image' && (
              <label className="dcb-upload-zone" style={{ padding: 10 }}>
                <span className="dcb-upload-icon">🖼</span>
                <span>Upload Background Image</span>
                <input type="file" accept="image/*" onChange={e => readFile(e, 'bgImageUrl')} hidden />
              </label>
            )}
            <div className="dcb-divider" />
            <p className="dcb-subsection-label">Watermark</p>
            <label className="dcb-upload-zone" style={{ padding: 10 }}>
              <span>💧 Upload Watermark Image</span>
              <input type="file" accept="image/*" onChange={e => readFile(e, 'watermarkUrl')} hidden />
            </label>
            {cfg.watermarkUrl && (
              <FRow label={`Opacity (${Math.round(cfg.watermarkOpacity * 100)}%)`}>
                <input type="range" className="dcb-range" min={0.02} max={0.3} step={0.01}
                  value={cfg.watermarkOpacity}
                  onChange={e => set('watermarkOpacity', +e.target.value)} />
              </FRow>
            )}
          </Section>
          )}

          {/* Signature Blocks — not needed for ID card */}
          {cfg.certType !== 'idcard' && (
          <Section title="Signature Blocks" open={sections.signatures} onToggle={() => toggle('signatures')}>
            {cfg.signatures.map((sig, idx) => (
              <div key={idx} className="dcb-sig-row">
                <input
                  className="dcb-input"
                  value={sig.label}
                  onChange={e => updateSig(idx, e.target.value)}
                  placeholder="Signature label"
                />
                <select
                  className="dcb-input"
                  style={{ maxWidth: 110 }}
                  value={sig.align || 'left'}
                  onChange={e => updateSigAlign(idx, e.target.value)}
                  title="Position"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
                <button className="dcb-remove" onClick={() => removeSig(idx)} title="Remove">×</button>
              </div>
            ))}
            <button className="dcb-add-btn" onClick={addSig}>+ Add Signature Block</button>
          </Section>
          )}

          {/* Footer — not needed for ID card */}
          {cfg.certType !== 'idcard' && (
          <Section title="Footer" open={sections.footer} onToggle={() => toggle('footer')}>
            <FRow label="Enable Footer">
              <label className="dcb-toggle">
                <input
                  type="checkbox"
                  checked={!!cfg.footerEnabled}
                  onChange={e => set('footerEnabled', e.target.checked)}
                />
                <span className="dcb-toggle-track" />
              </label>
            </FRow>

            {cfg.footerEnabled && (
              <>
                <FRow label="Footer Text">
                  <textarea
                    className="dcb-input"
                    rows={2}
                    style={{ resize: 'vertical', fontFamily: 'inherit' }}
                    value={cfg.footerText || ''}
                    placeholder="e.g. School Address | Ph: +91 XXXXX | www.school.com"
                    onChange={e => set('footerText', e.target.value)}
                  />
                </FRow>

                <FRow label="Divider Line">
                  <label className="dcb-toggle">
                    <input
                      type="checkbox"
                      checked={!!cfg.footerDivider}
                      onChange={e => set('footerDivider', e.target.checked)}
                    />
                    <span className="dcb-toggle-track" />
                  </label>
                </FRow>

                <FRow label="Alignment">
                  <div className="dcb-align-group">
                    {['left', 'center', 'right'].map(a => (
                      <button
                        key={a}
                        className={`dcb-align-btn${cfg.footerAlignment === a ? ' active' : ''}`}
                        onClick={() => set('footerAlignment', a)}
                      >
                        {a === 'left' ? '◀ Left' : a === 'center' ? '■ Center' : 'Right ▶'}
                      </button>
                    ))}
                  </div>
                </FRow>

                <div className="dcb-pair">
                  <FRow label="Font Size">
                    <div className="dcb-num-input">
                      <input
                        type="number" min={8} max={16}
                        value={cfg.footerFontSize || 11}
                        onChange={e => set('footerFontSize', +e.target.value)}
                      />
                      <span>px</span>
                    </div>
                  </FRow>
                  <FRow label="Text Color">
                    <input
                      type="color"
                      className="dcb-color"
                      value={cfg.footerColor || '#555555'}
                      onChange={e => set('footerColor', e.target.value)}
                    />
                  </FRow>
                </div>
              </>
            )}
          </Section>
          )}

        </div>

        {/* ══ RIGHT PANEL ══ */}
        <div className="dcb-right">

          {/* Template Library */}
          <div className="dcb-library">
            <div className="dcb-library-hdr" onClick={() => setShowLibrary(v => !v)}>
              <span>📚 Template Library</span>
              <span>{showLibrary ? '▲' : '▼'}</span>
            </div>
            {showLibrary && (
              <div className="dcb-lib-scroll">
                {TEMPLATE_LIBRARY.filter(tmpl => tmpl.category === cfg.certType).map(tmpl => (
                  <div
                    key={tmpl.id}
                    className="dcb-lib-card"
                    style={{ '--card-color': tmpl.color }}
                    onClick={() => loadTemplate(tmpl)}
                    title={tmpl.description || tmpl.name}
                  >
                    <div className="dcb-lib-badge" style={{ background: tmpl.color }}>
                      {tmpl.badge}
                    </div>
                    <div className="dcb-lib-name">{tmpl.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* A4 Preview */}
          <div className="dcb-preview-wrap">
            <div className="dcb-preview-hdr">
              <span>📄 LIVE A4 PREVIEW</span>
              <span className="dcb-preview-note">Sample student data shown</span>
            </div>
            <div className="dcb-preview-scroll" ref={scrollRef}>
              <div
                className="dcb-a4-positioner"
                style={{ width: Math.round(794 * a4Scale) }}
              >
                <div
                  ref={previewRef}
                  className="dcb-a4"
                  style={{ transform: `scale(${a4Scale})` }}
                  dangerouslySetInnerHTML={{ __html: previewHTML }}
                />
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default DynamicCertificateBuilder;

// Named export so CreateCertificate and MyTemplates can render previews
export { buildCertHTML as renderCertificateForStudent };
